import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import {
    COLORS,
    cardSx,
    FinanceKpiTile,
} from "../shared/FinanceUiKit";
import { getBilledAmount, hasVatIncluded } from "../shared/vatDisplay";
import { bankRowsForInvoice, isForeignCountry, resolveCustomerCountry } from "../shared/bankAccounts";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import {
    Alert,
    Avatar,
    Card,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    MenuItem,
    Tab,
    Tabs,
    TextField,
    Tooltip,
} from "@mui/material";

import {
    AccountBalanceWallet,
    AssignmentReturn,
    ArrowDownward,
    ArrowUpward,
    BusinessCenter,
    CheckCircle,
    CloudDownload,
    Description,
    Edit,
    Link as LinkIcon,
    Paid,
    PictureAsPdf,
    ReceiptLong,
    Search,
    Timeline,
    UploadFile,
    Visibility,
    WarningAmber,
    WorkspacePremium,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import FinanceStatusChip from "../components/FinanceStatusChip";
import { formatCurrency, formatPercent, formatDate } from "../utils/formatter";

import {
    financeText,
    getFinanceLanguage,
    saveFinanceLanguage,
} from "finance-language";
import { appAlert } from "components/AppDialog";
// Koyu hero bandı için (yedekten geri yüklendi)
import { ArrowBack } from "@mui/icons-material";
import { CalendarMonth } from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL;

const RISK_OPTIONS = [
    {
        value: "normal",
        label: "Normal",
        color: "#027A48",
        background: "#ECFDF3",
        border: "#ABEFC6",
    },
    {
        value: "overdue",
        label: "Overdue",
        color: "#B54708",
        background: "#FFFAEB",
        border: "#FEDF89",
    },
    {
        value: "doubtful",
        label: "Doubtful",
        color: "#6941C6",
        background: "#F4F3FF",
        border: "#D9D6FE",
    },
    {
        value: "bad_debt",
        label: "Bad Debt",
        color: "#B42318",
        background: "#FEF3F2",
        border: "#FECDCA",
    },
    {
        value: "written_off",
        label: "Written Off",
        color: "#344054",
        background: "#F2F4F7",
        border: "#D0D5DD",
    },
];

const normalizeRiskStatus = (value) => {
    const normalized = String(value || "normal")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");

    const aliases = {
        watch: "overdue",
        high: "doubtful",
        high_risk: "doubtful",
        highrisk: "doubtful",
        legal: "bad_debt",
        write_off: "written_off",
        writeoff: "written_off",
    };

    const resolvedStatus = aliases[normalized] || normalized;

    return RISK_OPTIONS.some((option) => option.value === resolvedStatus)
        ? resolvedStatus
        : "normal";
};

const getCollectionRisk = (item) => {
    const risk =
        item?.collectionRisk ||
        item?.collection_risk ||
        item?.invoice?.collectionRisk ||
        item?.invoice?.collection_risk ||
        {};

    return {
        ...risk,
        status: normalizeRiskStatus(
            risk?.status ||
            item?.riskStatus ||
            item?.risk_status ||
            item?.invoice?.riskStatus ||
            item?.invoice?.risk_status ||
            "normal",
        ),
        reason: risk?.reason || "",
        notes: risk?.notes || "",
        expectedRecoveryAmount: toNumber(
            risk?.expectedRecoveryAmount ?? risk?.expected_recovery_amount,
        ),
        provisionAmount: toNumber(risk?.provisionAmount ?? risk?.provision_amount),
        writtenOffAmount: toNumber(
            risk?.writtenOffAmount ?? risk?.written_off_amount,
        ),
    };
};

const tableWrapperSx = {
    "& .MuiTableContainer-root": {
        border: `1px solid ${COLORS.border}`,
        borderRadius: "10px",
        overflowX: "auto",
        boxShadow: "none",
    },
    "& table": {
        minWidth: 980,
        borderCollapse: "separate",
        borderSpacing: 0,
    },
    "& table thead": {
        background: "#F8FAFC",
    },
    "& table thead th": {
        background: "transparent",
        color: "#475467 !important",
        fontSize: "10px !important",
        fontWeight: "800 !important",
        letterSpacing: ".085em",
        paddingTop: "15px !important",
        paddingBottom: "15px !important",
        borderBottom: `1px solid ${COLORS.border} !important`,
    },
    "& table tbody td": {
        paddingTop: "14px !important",
        paddingBottom: "14px !important",
        borderBottom: "1px solid #F0F2F5 !important",
        verticalAlign: "middle",
    },
    "& table tbody tr": {
        background: "#FFFFFF",
        transition: "background .18s ease, box-shadow .18s ease",
    },
    "& table tbody tr:nth-of-type(even)": {
        background: "#FCFCFD",
    },
    "& table tbody tr:hover": {
        background: "#F5F8FF !important",
        boxShadow: "inset 3px 0 0 #175CD3",
    },
    "& table tbody tr:last-child td": {
        borderBottom: "0 !important",
    },
};

const getValue = (row, camel, snake, fallback = "") =>
    row?.[camel] ?? row?.[snake] ?? fallback;

const getEntityId = (value) => {
    if (!value) return null;
    if (typeof value === "string" || typeof value === "number")
        return String(value);
    return String(value?._id || value?.id || value?.$oid || "") || null;
};

const getFinanceJobId = (job, routeId) =>
    getEntityId(
        job?.financeJobId ||
        job?.finance_job_id ||
        job?.jobFinanceId ||
        job?.job_finance_id ||
        routeId,
    );

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const firstDefinedNumber = (...values) => {
    const value = values.find(
        (item) => item !== undefined && item !== null && item !== "",
    );
    return toNumber(value);
};

const normalizeCurrencyCode = (currency) => {
    const code = String(currency || "EUR")
        .trim()
        .toUpperCase();
    if (code === "EURO") return "EUR";
    if (code === "TL") return "TRY";
    return code;
};

const getCurrency = (job) =>
    normalizeCurrencyCode(
        job?.sellingRateCurrency ||
        job?.selling_rate_currency ||
        job?.currency ||
        job?.invoiceCurrency ||
        job?.invoice_currency ||
        job?.financialSummary?.sellingRateCurrency ||
        job?.financial_summary?.selling_rate_currency ||
        job?.financialSummary?.totalCurrency ||
        job?.financial_summary?.total_currency ||
        job?.freightCharges?.totalCurrency ||
        job?.freight_charges?.total_currency ||
        "EUR",
    );

const getCostCurrency = (job) => {
    const items =
        [
            job?.invoiceItems,
            job?.invoice_items,
            job?.cargoDetails,
            job?.cargo_details,
        ].find(Array.isArray) || [];
    const itemWithCurrency = items.find(
        (item) => item?.costCurrency || item?.cost_currency,
    );
    const itemCurrency =
        itemWithCurrency?.costCurrency || itemWithCurrency?.cost_currency;

    return normalizeCurrencyCode(
        job?.costCurrency ||
        job?.cost_currency ||
        job?.totalCostCurrency ||
        job?.total_cost_currency ||
        itemCurrency ||
        job?.financialSummary?.costCurrency ||
        job?.financial_summary?.cost_currency ||
        job?.financialSummary?.totalCurrency ||
        job?.financial_summary?.total_currency ||
        "EUR",
    );
};

const getInvoiceOrigin = (job) =>
    String(job?.invoiceOrigin || job?.invoice_origin || "tms")
        .trim()
        .toLowerCase() === "manual"
        ? "manual"
        : "tms";

// A finance-job Mongo id is not a manual-invoice id.  Keep this lookup
// deliberately narrow so a stale `invoiceOrigin: manual` label cannot route a
// normal job to `/finance/manual-invoices/:id`.
const getDedicatedManualInvoiceId = (record) =>
    getEntityId(record?.manualInvoiceId || record?.manual_invoice_id);

const getSourceType = (job) => {
    const explicit = String(
        job?.sourceType ||
        job?.source_type ||
        job?.transportMode ||
        job?.transport_mode ||
        "",
    ).toLowerCase();
    if (["road", "ocean", "air"].includes(explicit)) return explicit;

    const reference = String(
        job?.shipmentNo || job?.shipment_no || job?.jobNo || job?.job_no || "",
    ).toUpperCase();
    if (reference.startsWith("AHFL")) return "air";
    if (reference.startsWith("OHFL")) return "ocean";
    return "road";
};

const getRevenue = (job) =>
    firstDefinedNumber(
        job?.invoiceSalesAmount,
        job?.salesAmount,
        job?.customerInvoiceAmount,
        job?.actualRevenueTotal,
        job?.actual_revenue_total,
        job?.totalAmount,
        job?.amount,
        job?.financialSummary?.totalSellingRate,
        job?.financial_summary?.total_selling_rate,
    );

const getBuyingRate = (job) =>
    firstDefinedNumber(
        job?.buyingRate,
        job?.buying_rate,
        job?.financialSummary?.buyingRate,
        job?.financial_summary?.buying_rate,
        job?.freightCharges?.buyingRate,
        job?.freight_charges?.buying_rate,
    );

const getOperationalCost = (job) => {
    const summary = job?.financialSummary || job?.financial_summary || {};
    const freightCharges = job?.freightCharges || job?.freight_charges || {};

    const explicitOperationalCost = firstDefinedNumber(
        job?.operationalCost,
        job?.operational_cost,
        summary?.operationalCost,
        summary?.operational_cost,
    );

    if (explicitOperationalCost > 0) return explicitOperationalCost;

    const roadCost =
        firstDefinedNumber(summary?.shipmentCostTotal) +
        firstDefinedNumber(summary?.convertedLinehaulCost) +
        firstDefinedNumber(summary?.convertedHandlingCost);

    const oceanCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.oceanFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const airCost =
        firstDefinedNumber(summary?.pickupTotal) +
        firstDefinedNumber(summary?.othcTotal) +
        firstDefinedNumber(summary?.airFreightTotal) +
        firstDefinedNumber(summary?.dthcTotal) +
        firstDefinedNumber(summary?.handlingTotal) +
        firstDefinedNumber(summary?.deliveryTotal);

    const freightChargeCost =
        firstDefinedNumber(freightCharges?.pickupTotal) +
        firstDefinedNumber(freightCharges?.othc?.total) +
        firstDefinedNumber(freightCharges?.oceanFreight?.total) +
        firstDefinedNumber(freightCharges?.airFreight?.total) +
        firstDefinedNumber(freightCharges?.dthc?.total) +
        firstDefinedNumber(freightCharges?.handling?.total) +
        firstDefinedNumber(freightCharges?.deliveryTotal);

    if (roadCost > 0) return roadCost;
    if (oceanCost > 0) return oceanCost;
    if (airCost > 0) return airCost;
    if (freightChargeCost > 0) return freightChargeCost;
    return 0;
};

const getCost = (job) => {
    const explicitTotalCost = firstDefinedNumber(
        job?.actualCostTotal,
        job?.actual_cost_total,
        job?.totalCost,
        job?.total_cost,
        job?.financialSummary?.totalCost,
        job?.financial_summary?.total_cost,
    );

    const includesBuyingRate =
        job?.totalCostIncludesBuyingRate === true ||
        job?.total_cost_includes_buying_rate === true ||
        job?.financialSummary?.totalCostIncludesBuyingRate === true ||
        job?.financial_summary?.total_cost_includes_buying_rate === true;

    const invoiceItemCost = Array.isArray(job?.invoiceItems)
        ? job.invoiceItems.reduce(
            (sum, item) =>
                sum +
                firstDefinedNumber(
                    item?.totalCost,
                    item?.cost,
                    item?.unitCost,
                    item?.buyingRate,
                ),
            0,
        )
        : 0;

    if (explicitTotalCost > 0) {
        return includesBuyingRate
            ? explicitTotalCost
            : explicitTotalCost + getBuyingRate(job);
    }

    if (invoiceItemCost > 0) return invoiceItemCost;

    return getOperationalCost(job) + getBuyingRate(job);
};

// `convert(amount, fromCurrency, toCurrency)` is optional. A job's revenue
// currency and its cost currency routinely differ (carrier cost quoted in USD
// against a customer billed in EUR/TRY), and subtracting the raw numbers in
// that case produces a meaningless profit -- and therefore a meaningless
// margin ratio. Passing a converter (see FinanceJobDetail's local
// `convertCurrency`) normalizes the cost into the revenue currency first;
// without one this falls back to the old raw subtraction, which is only
// correct when both currencies already match. Returns null when a rate is
// missing so callers can show "N/A" instead of a wrong number.
const getProfit = (job, convert) => {
    const revenue = getRevenue(job);
    const cost = getCost(job);

    if (typeof convert !== "function") return revenue - cost;

    const revenueCurrency = normalizeCurrencyCode(getCurrency(job));
    const costCurrency = normalizeCurrencyCode(getCostCurrency(job));

    if (revenueCurrency === costCurrency) return revenue - cost;

    const convertedCost = convert(cost, costCurrency, revenueCurrency);
    if (convertedCost === null || convertedCost === undefined) return null;

    return revenue - convertedCost;
};

const getMargin = (job, convert) => {
    const revenue = getRevenue(job);
    if (revenue <= 0) return 0;

    const profit = getProfit(job, convert);
    return profit === null ? null : (profit / revenue) * 100;
};

// Converts EVERY currency bucket of a `{currency: amount}` map into
// `targetCurrency` (even a single-entry bucket) and sums them. Used for
// numeric comparisons like profit/margin and collection RATIOS, where the two
// sides must land in the SAME currency even when each side is individually
// single-currency -- e.g. invoices totalled in EUR against payments recorded
// in TRY: neither bucket alone is "mixed", but dividing one by the other
// without converting is still wrong. Returns null if a rate is missing, so
// callers can fall back to a raw/unconverted figure.
const sumConvertedTo = (byCurrency, convert, targetCurrency = "EUR") => {
    const entries = Object.entries(byCurrency).filter(([, amount]) => amount !== 0);
    if (entries.length === 0) return 0;
    if (typeof convert !== "function") return null;

    let total = 0;

    for (const [currency, amount] of entries) {
        const converted = convert(amount, currency, targetCurrency);
        if (converted === null || converted === undefined) return null;
        total += converted;
    }

    return total;
};

// Currencies offered by the page's "Currency" display selector. It only
// changes which currency aggregated figures are SHOWN in -- it never filters
// records and never rewrites a single invoice's own stored currency.
const CURRENCY_OPTIONS = ["EUR", "USD", "TRY", "GBP"];

const formatCompact = (value, currency = "EUR") =>
    new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    }).format(Number(value || 0));

const pdfSafeText = (value, fallback = "—") =>
    String(value ?? fallback)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[ğĞ]/g, "g")
        .replace(/[şŞ]/g, "s")
        .replace(/[ıİ]/g, "i")
        .replace(/[çÇ]/g, "c")
        .replace(/[öÖ]/g, "o")
        .replace(/[üÜ]/g, "u");

const loadPdfLetterhead = (url) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous";
        image.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            canvas.getContext("2d").drawImage(image, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        };
        image.onerror = () => reject(new Error(financeText("Invoice letterhead could not be loaded.")));
        image.src = url;
    });

const getPdfItems = (invoice, job, currency) => {
    const source = [
        invoice?.invoiceItems,
        invoice?.invoice_items,
        invoice?.items,
        job?.invoiceItems,
        job?.invoice_items,
        job?.cargoDetails,
        job?.cargo_details,
    ].find(Array.isArray) || [];

    return source.map((item, index) => {
        const quantity = firstDefinedNumber(
            item?.quantity,
            item?.qty,
            item?.unitQuantity,
            item?.unit_quantity,
            1,
        ) || 1;
        const unitPrice = firstDefinedNumber(
            item?.unitPrice,
            item?.unit_price,
            item?.sellingRate,
            item?.selling_rate,
            item?.amount && Number(item.amount) / quantity,
        );
        const amount = firstDefinedNumber(item?.amount, item?.totalAmount, unitPrice * quantity);
        const vatRate = firstDefinedNumber(item?.vatRate, item?.vat_rate, 0);
        const vatAmount = firstDefinedNumber(item?.vatAmount, item?.vat_amount, amount * vatRate / 100);

        return {
            no: index + 1,
            item: item?.invoiceItem || item?.commodity?.label || item?.commodity || item?.name || "Freight service",
            description: item?.description || item?.notes || "",
            quantity,
            unitPrice,
            amount,
            vatRate,
            vatAmount,
            currency: normalizeCurrencyCode(item?.currency || item?.sellingRateCurrency || currency),
        };
    });
};

const createFinanceJobDraftPdf = async (job, invoice) => {
    const source = invoice || job || {};
    const currency = normalizeCurrencyCode(
        source?.currency || source?.invoiceCurrency || source?.sellingRateCurrency || getCurrency(job),
    );
    const items = getPdfItems(source, job, currency);
    const calculatedSubtotal = items.reduce((sum, item) => sum + item.amount, 0);
    const calculatedVat = items.reduce((sum, item) => sum + item.vatAmount, 0);
    const subtotal = firstDefinedNumber(
        source?.subtotal,
        source?.subTotal,
        source?.totalAmount,
        source?.invoiceSalesAmount,
        calculatedSubtotal,
    );
    const vatTotal = firstDefinedNumber(source?.vatAmount, source?.totalVatAmount, calculatedVat);
    const grandTotal = firstDefinedNumber(
        source?.totalWithVat,
        source?.grandTotal,
        source?.invoiceTotal,
        subtotal + vatTotal,
    );
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const PAGE_TOP = 45;
    let letterhead = null;

    try {
        letterhead = await loadPdfLetterhead("/images/letterhead.png");
    } catch (error) {
        console.warn(error.message);
    }

    const drawLetterhead = () => {
        if (letterhead) doc.addImage(letterhead, "PNG", 0, 0, pageWidth, pageHeight);
    };
    const addPageWhenNeeded = (lastY, requiredHeight = 60) => {
        if (lastY + requiredHeight <= pageHeight - 20) return lastY + 10;
        doc.addPage();
        drawLetterhead();
        return PAGE_TOP;
    };
    const tableTheme = {
        theme: "grid",
        styles: { fontSize: 9, lineWidth: 0.5, cellPadding: 3 },
        headStyles: { fillColor: [16, 28, 54], textColor: 255, fontStyle: "bold" },
        margin: { top: PAGE_TOP },
    };

    drawLetterhead();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("DRAFT INVOICE", pageWidth / 2, 35, { align: "center" });
    doc.setLineWidth(0.7);
    doc.line(50, 42, 160, 42);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Date: ${formatDate(source?.invoiceDate || source?.createdAt || new Date())}`, pageWidth - 20, 35, { align: "right" });

    doc.setFont("helvetica", "bold");
    doc.text("Sender Information", pageWidth / 2, 50, { align: "center" });
    autoTable(doc, {
        startY: 55,
        head: [["Field", "Details", "Field", "Details"]],
        body: [["Company", "Filenders Lojistik ve Teknoloji A.S", "Phone", "+90 324 503 84 33"], ["Email", "accounting@filenders.com", "Payment Notice", "control.finance@filenders.com"], ["Tax No", "3871329803", "Job No", pdfSafeText(job?.jobNo || job?.shipmentNo || source?.shipmentNo || source?.invoiceNo)]],
        ...tableTheme,
    });

    let y = addPageWhenNeeded(doc.lastAutoTable.finalY, 72);
    doc.setFont("helvetica", "bold");
    doc.text("Customer Information", pageWidth / 2, y, { align: "center" });
    autoTable(doc, {
        startY: y + 5,
        head: [["Field", "Details", "Field", "Details"]],
        body: [["Invoice No", pdfSafeText(source?.invoiceNumber || source?.invoiceNo || source?.invoice_no || "Draft"), financeText("Customer"), pdfSafeText(source?.customerName || source?.customer?.companyName || job?.customerName)], ["Email", pdfSafeText(source?.email || source?.customer?.email), "Phone", pdfSafeText(source?.phone || source?.customer?.phone)], ["Tax ID", pdfSafeText(source?.taxID || source?.taxId || source?.customer?.taxID), "Invoice Address", pdfSafeText(source?.billingAddress || source?.customer?.billingAddress || source?.customer?.address)]],
        ...tableTheme,
    });

    y = addPageWhenNeeded(doc.lastAutoTable.finalY, 70);
    autoTable(doc, {
        startY: y,
        head: [["#", "Invoice Item", "Description", "Qty", "Unit Price", "VAT", "Amount"]],
        body: items.length
            ? items.map((item) => [item.no, pdfSafeText(item.item), pdfSafeText(item.description, ""), item.quantity, formatCurrency(item.unitPrice, item.currency), `${item.vatRate.toFixed(2)}%`, formatCurrency(item.amount + item.vatAmount, item.currency)])
            : [[1, "Freight service", "", 1, formatCurrency(subtotal, currency), "0.00%", formatCurrency(grandTotal, currency)]],
        ...tableTheme,
        styles: { ...tableTheme.styles, halign: "center" },
    });

    const totalsY = addPageWhenNeeded(doc.lastAutoTable.finalY, 35);
    doc.setFont("helvetica", "normal");
    doc.text(`Subtotal: ${formatCurrency(subtotal, currency)}`, pageWidth - 20, totalsY, { align: "right" });
    doc.text(`VAT: ${formatCurrency(vatTotal, currency)}`, pageWidth - 20, totalsY + 6, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.text(`Total Amount: ${formatCurrency(grandTotal, currency)}`, pageWidth - 20, totalsY + 12, { align: "right" });

    doc.addPage();
    drawLetterhead();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("BANK DETAILS", pageWidth / 2, PAGE_TOP - 10, { align: "center" });
    autoTable(doc, {
        startY: PAGE_TOP,
        head: [["BANK", "CURRENCY", "IBAN", "BRANCH", "SWIFT"]],
        body: bankRowsForInvoice(
            [["T.GARANTI BANKASI", "USD", "TR20 0006 2001 1730 0009 0929 99", "MERSIN SANAYI", "TGBATRISXXX"], ["T.GARANTI BANKASI", "EUR", "TR90 0006 2001 1730 0009 0930 00", "MERSIN SANAYI", "TGBATRISXXX"], ["T.GARANTI BANKASI", "TRY", "TR94 0006 2001 1730 0006 2962 24", "MERSIN SANAYI", ""]],
            { currency, foreign: isForeignCountry(await resolveCustomerCountry(invoice, job)) },
        ),
        ...tableTheme,
    });
    autoTable(doc, {
        startY: doc.lastAutoTable.finalY + 10,
        head: [["Payment Terms and Notification"]],
        body: [["Payments must be made in full within the payment term specified on this invoice. Upon completion of payment, the SWIFT transfer confirmation must be sent to accounting@filenders.com."]],
        ...tableTheme,
        styles: { ...tableTheme.styles, cellPadding: 6 },
    });

    return doc;
};

const EmptyState = ({ title, icon }) => (
    <MDBox py={7} textAlign="center">
        <MDBox
            width={56}
            height={56}
            mx="auto"
            mb={1.5}
            borderRadius="10px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
                background: COLORS.blueLight,
                color: COLORS.blue,
                border: "1px solid #B2DDFF",
            }}
        >
            {icon}
        </MDBox>

        <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ color: COLORS.text }}
        >
            {title}
        </MDTypography>
    </MDBox>
);

// Görünüm Finance kitindeki KPI kartıyla aynı; altta beklenen değere göre sapma rozeti
const MetricCard = ({ delta = 0, ...props }) => {
    const positive = Number(delta) >= 0;
    return (
        <FinanceKpiTile
            {...props}
            footer={
                <MDBox display="flex" alignItems="center" gap={0.65}>
                    <MDBox
                        display="flex"
                        alignItems="center"
                        px={0.8}
                        py={0.25}
                        borderRadius="6px"
                        sx={{
                            color: positive ? COLORS.green : COLORS.red,
                            background: positive ? COLORS.greenLight : COLORS.redLight,
                            fontSize: "12px",
                            fontWeight: 700,
                        }}
                    >
                        {positive ? (
                            <ArrowUpward sx={{ fontSize: "14px !important", mr: 0.25 }} />
                        ) : (
                            <ArrowDownward sx={{ fontSize: "14px !important", mr: 0.25 }} />
                        )}
                        {Math.abs(Number(delta)).toFixed(1)}%
                    </MDBox>
                    <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                        {financeText("vs expected")}
                    </MDTypography>
                </MDBox>
            }
        />
    );
};

const FinanceTabLabel = ({ icon, label, count }) => {
    const hasCount = count !== null && count !== undefined;

    return (
        <MDBox
            component="span"
            className="finance-tab-label"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            width="100%"
            minWidth={0}
        >
            <MDBox
                component="span"
                className="finance-tab-icon"
                width={30}
                height={30}
                borderRadius="9px"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                sx={{
                    color: "inherit",
                    background: "rgba(255, 255, 255, 0.78)",
                    border: "1px solid rgba(208, 213, 221, 0.82)",
                    transition:
                        "background .2s ease, border-color .2s ease, box-shadow .2s ease",
                    "& .MuiSvgIcon-root": {
                        fontSize: "17px !important",
                    },
                }}
            >
                {icon}
            </MDBox>

            <MDTypography
                component="span"
                variant="button"
                className="finance-tab-text"
                fontWeight="bold"
                sx={{
                    color: "inherit",
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                }}
            >
                {financeText(label)}
            </MDTypography>

            {hasCount ? (
                <MDBox
                    component="span"
                    className="finance-tab-count"
                    minWidth={24}
                    height={24}
                    px={0.7}
                    borderRadius="999px"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                    sx={{
                        color: COLORS.subtext,
                        background: "#EAECF0",
                        border: "1px solid #D0D5DD",
                        fontSize: "11px",
                        fontWeight: 800,
                        lineHeight: 1,
                        transition: "all .2s ease",
                    }}
                >
                    {count}
                </MDBox>
            ) : null}
        </MDBox>
    );
};

const FinanceJobDetail = () => {
    const { id } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();

    const [financeLanguage, setFinanceLanguageState] = useState(getFinanceLanguage);

    const changeFinanceLanguage = (event) => {
        const nextLanguage = event.target.value === "tr" ? "tr" : "en";
        saveFinanceLanguage(nextLanguage);
        setFinanceLanguageState(nextLanguage);
    };

    const [job, setJob] = useState(state?.job || null);
    const [loading, setLoading] = useState(!state?.job);
    const [tab, setTab] = useState(0);
    const [exchangeRates, setExchangeRates] = useState(null);
    const [selectedCurrency, setSelectedCurrency] = useState("EUR");
    const [returnableInvoice, setReturnableInvoice] = useState(null);
    const [fetchedOriginalInvoice, setFetchedOriginalInvoice] = useState(null);
    const [fetchedReturnInvoices, setFetchedReturnInvoices] = useState([]);
    const [loadingReturns, setLoadingReturns] = useState(false);
    const [returnError, setReturnError] = useState("");
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const [detailError, setDetailError] = useState("");
    const [refreshCounter, setRefreshCounter] = useState(0);

    const [incomingLinkOpen, setIncomingLinkOpen] = useState(false);
    const [incomingInvoices, setIncomingInvoices] = useState([]);
    const [loadingIncoming, setLoadingIncoming] = useState(false);
    const [incomingError, setIncomingError] = useState("");
    const [incomingSearchQuery, setIncomingSearchQuery] = useState("");
    const [selectedIncoming, setSelectedIncoming] = useState(null);
    const [linkingIncoming, setLinkingIncoming] = useState(false);
    const [linkIncomingError, setLinkIncomingError] = useState("");

    const token = localStorage.getItem("token");

    useEffect(() => {
        let active = true;

        const fetchJob = async () => {
            try {
                if (!state?.job) setLoading(true);
                setDetailError("");

                // A normal finance-job id must never be tried against the
                // manual-invoice endpoint. They are different Mongo records;
                // the old fallback caused a misleading "Manual invoice not found"
                // response for regular TMS invoices.
                const stateJobId = getEntityId(state?.job);
                const financeJobId = getFinanceJobId(state?.job, null);
                const manualInvoiceId = getDedicatedManualInvoiceId(state?.job);

                // `invoiceOrigin` alone is not enough here: list data can
                // inherit that label from another record. Only a dedicated
                // manual-invoice reference is allowed to use the manual API.
                const isStateManualInvoice =
                    getInvoiceOrigin(state?.job) === "manual" &&
                    Boolean(manualInvoiceId);

                const candidateIds = [
                    ...new Set([id, stateJobId, financeJobId].filter(Boolean)),
                ];

                // isStateManualInvoice is only known when we arrived here via
                // navigate() with router state. On a hard refresh, a
                // bookmarked link, or opening the URL in a new tab, `state`
                // is undefined and that flag is always false — so this used
                // to only ever try /finance/jobs/:id, which 404s for a
                // manual invoice (it isn't a real shipment/job record). Try
                // the manual-invoices endpoint as a fallback in that
                // uncertain case instead of giving up.
                const detailPaths = isStateManualInvoice
                    ? [
                        ...new Set(
                            [
                                manualInvoiceId,
                            ]
                                .filter(Boolean)
                                .map(
                                    (manualInvoiceId) =>
                                        `/finance/manual-invoices/${encodeURIComponent(manualInvoiceId)}`,
                                ),
                        ),
                    ]
                    : [
                        ...candidateIds.map(
                            (financeJobId) =>
                                `/finance/jobs/${encodeURIComponent(financeJobId)}`,
                        ),
                        ...candidateIds.map(
                            (manualId) =>
                                `/finance/manual-invoices/${encodeURIComponent(manualId)}`,
                        ),
                    ];
                let data = null;
                let lastError = "Finance job detail fetch failed";

                for (const path of detailPaths) {
                    const response = await fetch(`${API_URL}${path}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });

                    if (!response.ok) {
                        const errorPayload = await response.json().catch(() => null);
                        lastError =
                            errorPayload?.message ||
                            errorPayload?.error ||
                            `Finance job detail request failed (${response.status})`;
                        continue;
                    }
                    data = await response.json();
                    break;
                }

                if (!data) {
                    throw new Error(lastError);
                }

                const responseData = data?.data ?? data;
                const financeJob = responseData?.job ?? responseData?.data ?? responseData;

                if (!financeJob || typeof financeJob !== "object") {
                    throw new Error("Finance job detail response is empty");
                }

                if (active) setJob(financeJob);
            } catch (error) {
                console.error("Finance job detail error:", error);
                if (active) {
                    setDetailError(error.message || "Finance job detail could not be loaded");
                    if (!state?.job) setJob(null);
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchJob();

        return () => {
            active = false;
        };
    }, [id, state?.job, token, refreshCounter]);

    useEffect(() => {
        const loadExchangeRates = async () => {
            try {
                const response = await fetch(
                    "https://api.exchangerate-api.com/v4/latest/USD",
                );
                if (!response.ok) throw new Error("Exchange rates could not be loaded");
                const data = await response.json();
                setExchangeRates(data?.rates || null);
            } catch (error) {
                console.error("Exchange rate fetch error:", error);
                setExchangeRates(null);
            }
        };

        loadExchangeRates();
    }, []);

    // Memoized so hooks that depend on it (e.g. collectionRate) only
    // recompute when the exchange rates actually change.
    const convertCurrency = useCallback(
        (amount, fromCurrency, toCurrency) => {
            const value = Number(amount) || 0;
            const from = normalizeCurrencyCode(fromCurrency);
            const to = normalizeCurrencyCode(toCurrency);

            if (from === to) return value;
            if (!exchangeRates?.[from] || !exchangeRates?.[to]) return null;

            return (value / exchangeRates[from]) * exchangeRates[to];
        },
        [exchangeRates],
    );

    const currency = getCurrency(job);
    const financeJobId = getFinanceJobId(job, id);
    const costCurrency = getCostCurrency(job);

    const jobShipmentNo =
        job?.shipmentNo || job?.shipment_no || job?.jobNo || job?.job_no || "";

    // Mirrors IncomingInvoices' "Match Open Job" flow from the other
    // direction: instead of picking a job for an EDM invoice, this picks an
    // EDM invoice for the job already open here, so the financeJobId/
    // shipmentNo are already known and don't need a second lookup step.
    const fetchIncomingInvoices = async () => {
        setLoadingIncoming(true);
        setIncomingError("");

        try {
            const end = new Date();
            const start = new Date(end.getTime() - 59 * 24 * 60 * 60 * 1000);
            const params = new URLSearchParams({
                startDate: start.toISOString().slice(0, 10),
                endDate: end.toISOString().slice(0, 10),
            });

            const response = await fetch(`${API_URL}/edm/incoming-invoices?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const payload = await response.json().catch(() => ({}));

            if (!response.ok || !payload?.success) {
                throw new Error(
                    payload?.error || financeText("Incoming invoices could not be loaded."),
                );
            }

            setIncomingInvoices(Array.isArray(payload.invoices) ? payload.invoices : []);
        } catch (error) {
            setIncomingError(error.message || financeText("Incoming invoices could not be loaded."));
            setIncomingInvoices([]);
        } finally {
            setLoadingIncoming(false);
        }
    };

    const openIncomingLinkDialog = () => {
        setIncomingLinkOpen(true);
        setSelectedIncoming(null);
        setIncomingSearchQuery("");
        setLinkIncomingError("");
        fetchIncomingInvoices();
    };

    const closeIncomingLinkDialog = () => {
        if (linkingIncoming) return;
        setIncomingLinkOpen(false);
    };

    const filteredIncomingInvoices = useMemo(() => {
        const query = incomingSearchQuery.trim().toLowerCase();

        return incomingInvoices.filter((item) => {
            if (item.linked) return false;
            if (currency && item.currency && normalizeCurrencyCode(item.currency) !== currency) {
                return false;
            }
            if (!query) return true;

            return (
                (item.supplierName || "").toLowerCase().includes(query) ||
                (item.id || "").toLowerCase().includes(query)
            );
        });
    }, [incomingInvoices, incomingSearchQuery, currency]);

    const submitIncomingLink = async () => {
        if (!selectedIncoming) {
            setLinkIncomingError(financeText("Select an invoice to link."));
            return;
        }

        setLinkingIncoming(true);
        setLinkIncomingError("");

        try {
            const response = await fetch(`${API_URL}/finance/supplier-invoices`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    supplierName: selectedIncoming.supplierName,
                    invoiceNo: selectedIncoming.id,
                    invoiceDate: selectedIncoming.issueDate,
                    totalAmount: selectedIncoming.amount,
                    netPayableAmount: selectedIncoming.amount,
                    currency: selectedIncoming.currency,
                    financeJobId,
                    shipmentNo: jobShipmentNo,
                    jobNo: jobShipmentNo,
                    edmSourceUuid: selectedIncoming.uuid,
                }),
            });

            const payload = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(
                    payload?.message || financeText("This incoming invoice could not be linked."),
                );
            }

            setIncomingLinkOpen(false);
            setSelectedIncoming(null);
            setRefreshCounter((count) => count + 1);
        } catch (error) {
            setLinkIncomingError(
                error.message || financeText("This incoming invoice could not be linked."),
            );
        } finally {
            setLinkingIncoming(false);
        }
    };

    // Display-currency layer for the "Currency" selector. Every aggregated /
    // blended figure on this page (KPI cards, chart bars, liquidity rows,
    // variance cards, the narrative summary) is computed first in its own
    // natural currency and then converted here for rendering only, so the
    // underlying math is untouched and per-invoice row amounts keep showing
    // their OWN currency. Falls back to the source currency (correctly
    // labelled, never mislabelled as the selected one) while exchange rates
    // are still loading or a rate is missing.
    const toDisplayAmount = (amount, fromCurrency = currency) => {
        const source = normalizeCurrencyCode(fromCurrency);
        const converted = convertCurrency(amount, source, selectedCurrency);

        return converted === null || converted === undefined
            ? { amount: Number(amount) || 0, currency: source }
            : { amount: converted, currency: normalizeCurrencyCode(selectedCurrency) };
    };

    const formatDisplayCompact = (amount, fromCurrency = currency) => {
        const shown = toDisplayAmount(amount, fromCurrency);
        return formatCompact(shown.amount, shown.currency);
    };

    const formatDisplayExact = (amount, fromCurrency = currency) => {
        const shown = toDisplayAmount(amount, fromCurrency);
        return formatCurrency(shown.amount, shown.currency);
    };
    const rawRevenue = getRevenue(job);
    const rawCost = getCost(job);
    const convertedRevenue = convertCurrency(rawRevenue, currency, currency);
    const convertedCost = convertCurrency(rawCost, costCurrency, currency);
    const ratesAvailable = convertedRevenue !== null && convertedCost !== null;
    const revenue = convertedRevenue ?? rawRevenue;
    // Revenue stays ex-VAT for profit/margin; the headline selling rate shows
    // what the customer is billed when the invoice carries VAT.
    const vatIncludedJob = hasVatIncluded(job);
    const billedRevenue = getBilledAmount(job, revenue);
    const cost = convertedCost ?? 0;
    const rawOperationalCost = getOperationalCost(job);
    const rawBuyingRate = getBuyingRate(job);
    const operationalCost =
        convertCurrency(rawOperationalCost, costCurrency, currency) ?? 0;
    const buyingRate =
        convertCurrency(rawBuyingRate, costCurrency, currency) ?? 0;
    const profit = ratesAvailable ? revenue - cost : null;
    const margin =
        ratesAvailable && revenue > 0 ? (profit / revenue) * 100 : null;

    const expectedRevenue = firstDefinedNumber(
        job?.expectedRevenueTotal,
        job?.expected_revenue_total,
        revenue,
    );

    const rawExpectedCost = firstDefinedNumber(
        job?.expectedCostTotal,
        job?.expected_cost_total,
        rawCost,
    );

    const expectedCost =
        convertCurrency(rawExpectedCost, costCurrency, currency) ?? 0;

    const expectedProfit = ratesAvailable ? expectedRevenue - expectedCost : null;

    const revenueDelta = expectedRevenue
        ? ((revenue - expectedRevenue) / expectedRevenue) * 100
        : 0;

    const costDelta = expectedCost
        ? ((expectedCost - cost) / expectedCost) * 100
        : 0;

    const profitDelta =
        expectedProfit && profit !== null
            ? ((profit - expectedProfit) / Math.abs(expectedProfit)) * 100
            : 0;

    const isManualInvoice = getInvoiceOrigin(job) === "manual";
    const collectionRisk = getCollectionRisk(job);
    const riskOption =
        RISK_OPTIONS.find((option) => option.value === collectionRisk.status) ||
        RISK_OPTIONS[0];
    const riskTone =
        {
            normal: "green",
            overdue: "amber",
            doubtful: "purple",
            bad_debt: "red",
            written_off: "navy",
        }[collectionRisk.status] || "green";

    const customerInvoices = useMemo(() => {
        const linkedInvoices =
            job?.customerInvoices || job?.customer_invoices || job?.invoices;

        if (Array.isArray(linkedInvoices) && linkedInvoices.length) {
            return linkedInvoices;
        }

        // Manuel satış faturası aynı zamanda bu detay ekranının
        // müşteri faturası kaydıdır. Böylece toplamlar, tahsilat oranı
        // ve invoice tablosu TMS kayıtlarıyla aynı şekilde çalışır.
        if (
            job &&
            getInvoiceOrigin(job) === "manual" &&
            String(job?.invoiceType || "sales").toLowerCase() === "sales"
        ) {
            return [job];
        }

        return [];
    }, [job]);

    useEffect(() => {
        if (!customerInvoices.length) {
            setSelectedInvoiceId(null);
            return;
        }

        const stillExists = customerInvoices.some(
            (invoice) =>
                getEntityId(invoice?._id || invoice?.id) === selectedInvoiceId,
        );

        if (!stillExists) {
            setSelectedInvoiceId(
                getEntityId(customerInvoices[0]?._id || customerInvoices[0]?.id),
            );
        }
    }, [customerInvoices, selectedInvoiceId]);

    const embeddedOriginalInvoice = useMemo(() => {
        if (
            job &&
            getInvoiceOrigin(job) === "manual" &&
            String(job?.invoiceType || "sales").toLowerCase() === "sales"
        ) {
            return job;
        }

        const selectedInvoice = customerInvoices.find(
            (invoice) =>
                getEntityId(invoice?._id || invoice?.id) === selectedInvoiceId,
        );

        return (
            selectedInvoice ||
            customerInvoices.find(
                (invoice) =>
                    invoice?.documentType !== "return_invoice" &&
                    invoice?.document_type !== "return_invoice",
            ) ||
            null
        );
    }, [customerInvoices, job, selectedInvoiceId]);

    const originalInvoice = fetchedOriginalInvoice || embeddedOriginalInvoice;

    useEffect(() => {
        let active = true;

        const resolveOriginalInvoice = async () => {
            if (embeddedOriginalInvoice) {
                setFetchedOriginalInvoice(null);
                return;
            }

            if (!job || getInvoiceOrigin(job) === "manual") return;

            const shipmentId = String(
                job?.shipmentId || job?.shipment_id || job?._id || job?.id || "",
            );

            if (!shipmentId) return;

            try {
                const response = await fetch(`${API_URL}/finance/customer-invoices`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const payload = await response.json().catch(() => []);

                if (!response.ok) {
                    throw new Error(
                        payload?.error ||
                        payload?.message ||
                        financeText("Customer invoices could not be loaded."),
                    );
                }

                const records =
                    [
                        payload,
                        payload?.data,
                        payload?.data?.invoices,
                        payload?.invoices,
                    ].find(Array.isArray) || [];

                const matchedInvoice = records.find((invoice) => {
                    if (
                        invoice?.documentType === "return_invoice" ||
                        invoice?.document_type === "return_invoice"
                    ) {
                        return false;
                    }

                    const invoiceShipmentId = String(
                        invoice?.shipmentId || invoice?.shipment_id || "",
                    );
                    const invoiceId = String(invoice?._id || invoice?.id || "");

                    return invoiceShipmentId === shipmentId || invoiceId === shipmentId;
                });

                if (active) setFetchedOriginalInvoice(matchedInvoice || null);
            } catch (error) {
                console.error("Original customer invoice resolve error:", error);
                if (active) setFetchedOriginalInvoice(null);
            }
        };

        resolveOriginalInvoice();

        return () => {
            active = false;
        };
    }, [embeddedOriginalInvoice, job, token]);

    const originalInvoiceId =
        originalInvoice?._id ||
        originalInvoice?.id ||
        originalInvoice?.invoiceId ||
        originalInvoice?.invoice_id ||
        null;

    const openJobDraftPdf = async () => {
        const pdfWindow = window.open("", "_blank", "noopener,noreferrer");

        if (pdfWindow) {
            pdfWindow.document.write(
                "<title>Draft Invoice</title><p style='font-family:Arial,sans-serif;padding:24px'>Preparing draft invoice PDF...</p>",
            );
            pdfWindow.document.close();
        }

        try {
            // The invoice is preferred because it contains the actual saved
            // line items and totals. The job is only a fallback for a draft
            // that has not been saved yet.
            const doc = await createFinanceJobDraftPdf(job, originalInvoice);
            const pdfUrl = doc.output("bloburl");

            if (pdfWindow) {
                pdfWindow.location.replace(pdfUrl);
            } else {
                window.open(pdfUrl, "_blank", "noopener,noreferrer");
            }
        } catch (error) {
            console.error("Finance job draft PDF error:", error);
            pdfWindow?.close();
            appAlert(error.message || financeText("Draft invoice PDF could not be generated."));
        }
    };

    useEffect(() => {
        let active = true;

        const fetchReturnableInvoice = async () => {
            if (!originalInvoiceId) {
                setReturnableInvoice(null);
                setFetchedReturnInvoices([]);
                setReturnError("");
                return;
            }

            try {
                setLoadingReturns(true);
                setReturnError("");
                setReturnableInvoice(null);
                setFetchedReturnInvoices([]);

                const headers = { Authorization: `Bearer ${token}` };
                const [returnableResponse, historyResponse] = await Promise.all([
                    fetch(`${API_URL}/finance/invoices/${originalInvoiceId}/returnable`, {
                        headers,
                    }),
                    fetch(
                        `${API_URL}/finance/return-invoices?originalInvoiceId=${encodeURIComponent(originalInvoiceId)}`,
                        { headers },
                    ),
                ]);

                const payload = await returnableResponse.json().catch(() => ({}));

                if (!returnableResponse.ok) {
                    throw new Error(
                        payload?.error ||
                        payload?.message ||
                        financeText("Return information could not be loaded."),
                    );
                }

                const responseData = payload?.data?.data || payload?.data || payload;
                const data = responseData?.invoice
                    ? { ...responseData.invoice, ...responseData }
                    : responseData;

                const historyPayload = await historyResponse.json().catch(() => []);
                const history =
                    [
                        historyPayload,
                        historyPayload?.data,
                        historyPayload?.data?.returnInvoices,
                        historyPayload?.returnInvoices,
                    ].find(Array.isArray) || [];

                if (active) {
                    setReturnableInvoice(data);
                    setFetchedReturnInvoices(historyResponse.ok ? history : []);
                }
            } catch (error) {
                console.error("Returnable invoice fetch error:", error);
                if (active) {
                    setReturnableInvoice(null);
                    setFetchedReturnInvoices([]);
                    setReturnError(error.message);
                }
            } finally {
                if (active) setLoadingReturns(false);
            }
        };

        fetchReturnableInvoice();

        return () => {
            active = false;
        };
    }, [originalInvoiceId, token]);

    const supplierCosts = useMemo(
        () =>
            job?.supplierCosts || job?.supplier_costs || job?.supplierCostLines || [],
        [job],
    );

    const supplierInvoices = useMemo(
        () => job?.supplierInvoices || job?.supplier_invoices || [],
        [job],
    );

    const documents = useMemo(() => job?.documents || job?.files || [], [job]);

    const activities = useMemo(
        () => job?.activities || job?.activityLog || [],
        [job],
    );

    const financeTabs = useMemo(
        () => [
            {
                key: "overview",
                label: "Overview",
                description: "Core job information and finance workflow status.",
                icon: <BusinessCenter />,
                count: null,
            },
            {
                key: "customer-invoices",
                label: "Customer Invoices",
                description: "Billing, collection, balance and return invoice control.",
                icon: <ReceiptLong />,
                count: customerInvoices.length,
            },
            {
                key: "supplier-costs",
                label: "Supplier Costs",
                description: "Expected and actual operating cost variance tracking.",
                icon: <AccountBalanceWallet />,
                count: supplierCosts.length,
            },
            {
                key: "supplier-invoices",
                label: "Supplier Invoices",
                description:
                    "Supplier invoice approval, due date and payment workflow.",
                icon: <UploadFile />,
                count: supplierInvoices.length,
            },
            {
                key: "documents",
                label: "Documents",
                description: "Finance and shipment document archive for this job.",
                icon: <Description />,
                count: documents.length,
            },
            {
                key: "profitability",
                label: "Profitability",
                description: "Revenue, cost, profit, margin and variance analysis.",
                icon: <Paid />,
                count: null,
            },
            {
                key: "activity",
                label: "Activity",
                description: "Audit trail of finance actions and workflow events.",
                icon: <Timeline />,
                count: activities.length,
            },
        ],
        [
            activities.length,
            customerInvoices.length,
            documents.length,
            supplierCosts.length,
            supplierInvoices.length,
        ],
    );

    const activeFinanceTab = financeTabs[tab] || financeTabs[0];

    const collectionRate = useMemo(() => {
        // Invoiced and collected amounts were previously summed raw, so a job
        // with e.g. a EUR invoice and a TRY payment divided two unlike
        // currencies against each other and produced a nonsense collection %.
        // Group each side by its own currency and convert every bucket into
        // the job currency before dividing.
        const totalByCurrency = {};
        const paidByCurrency = {};

        customerInvoices.forEach((item) => {
            const invoiceCurrency = normalizeCurrencyCode(item?.currency || currency);

            totalByCurrency[invoiceCurrency] =
                (totalByCurrency[invoiceCurrency] || 0) +
                toNumber(item?.totalAmount || item?.total_amount || 0);
            paidByCurrency[invoiceCurrency] =
                (paidByCurrency[invoiceCurrency] || 0) +
                toNumber(item?.paidAmount || item?.paid_amount || 0);
        });

        const sumRaw = (byCurrency) =>
            Object.values(byCurrency).reduce((sum, amount) => sum + amount, 0);

        // Raw sums only as a fallback while exchange rates are still loading.
        const total =
            sumConvertedTo(totalByCurrency, convertCurrency, currency) ??
            sumRaw(totalByCurrency);
        const paid =
            sumConvertedTo(paidByCurrency, convertCurrency, currency) ??
            sumRaw(paidByCurrency);

        return total ? (paid / total) * 100 : 0;
    }, [customerInvoices, currency, convertCurrency]);

    const openCustomerInvoice = (invoice, editMode = false) => {
        const invoiceId = getEntityId(
            invoice?._id || invoice?.id || invoice?.invoiceId || invoice?.invoice_id,
        );

        if (!invoiceId) {
            appAlert(financeText("Customer invoice ID not found!"));
            return;
        }

        // Invoice navigation must be determined by the selected invoice.
        // Falling back to the job origin can send a normal customer invoice
        // to the manual-invoice endpoint with the wrong MongoDB id.
        const origin = getInvoiceOrigin(invoice);
        // A manual invoice row IS the manual invoice, so its own id is the one
        // to open (rows carry no separate manualInvoiceId).
        const manualInvoiceId =
            getDedicatedManualInvoiceId(invoice) ||
            (origin === "manual" ? invoiceId : "");

        if (origin === "manual" && manualInvoiceId) {
            // A manual invoice has no read-only screen, and its finance job page is
            // the one we are already on, so "View" opens the manual-invoice page
            // (issued invoices come up with their locked fields) just like "Edit".
            navigate(
                `/invoice/finance/manual-invoices/${manualInvoiceId}/edit`,
                {
                    state: {
                        invoice,
                        invoiceId: manualInvoiceId,
                        financeJobId,
                        editMode,
                    },
                },
            );
            return;
        }

        navigate("/create-invoice", {
            state: {
                openInvoiceModal: true,
                editMode,
                viewMode: !editMode,
                invoice,
                invoiceId,
                financeJobId,
                shipments: [job],
            },
        });
    };

    const selectInvoiceForReturns = (invoice) => {
        const invoiceId = getEntityId(invoice?._id || invoice?.id);

        if (!invoiceId) {
            appAlert(financeText("Customer invoice ID not found!"));
            return;
        }

        setSelectedInvoiceId(invoiceId);
    };

    const customerInvoiceTable = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("INVOICE NO"),
                    accessor: "invoiceNo",
                    Cell: ({ row }) => (
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.navy }}
                        >
                            {row.original.invoiceNo || row.original.invoice_no || "N/A"}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("INVOICE DATE"),
                    accessor: "invoiceDate",
                    Cell: ({ row }) =>
                        formatDate(row.original.invoiceDate || row.original.invoice_date),
                },
                {
                    Header: financeText("DUE DATE"),
                    accessor: "dueDate",
                    Cell: ({ row }) =>
                        formatDate(row.original.dueDate || row.original.due_date),
                },
                {
                    Header: financeText("AMOUNT"),
                    accessor: "totalAmount",
                    Cell: ({ row }) => (
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.blue }}
                        >
                            {formatCompact(
                                row.original.totalAmount || row.original.total_amount,
                                row.original.currency || currency,
                            )}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("PAID"),
                    accessor: "paidAmount",
                    Cell: ({ row }) => (
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.green }}
                        >
                            {formatCompact(
                                row.original.paidAmount || row.original.paid_amount,
                                row.original.currency || currency,
                            )}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("BALANCE"),
                    accessor: "balanceAmount",
                    Cell: ({ row }) => (
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.amber }}
                        >
                            {formatCompact(
                                row.original.balanceAmount || row.original.balance_amount,
                                row.original.currency || currency,
                            )}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("STATUS"),
                    accessor: "invoiceStatus",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={
                                row.original.invoiceStatus ||
                                row.original.invoice_status ||
                                "Draft"
                            }
                        />
                    ),
                },
                {
                    Header: financeText("ACTIONS"),
                    accessor: "actions",
                    Cell: ({ row }) => {
                        const invoice = row.original;
                        const invoiceId = getEntityId(invoice?._id || invoice?.id);
                        const isSelected = invoiceId === selectedInvoiceId;

                        return (
                            <MDBox display="flex" alignItems="center" gap={0.6}>
                                <Tooltip title={financeText("View customer invoice")} arrow>
                                    <span style={{ display: "inline-flex" }}><IconButton
                                        size="small"
                                        disabled={!invoiceId}
                                        onClick={() => openCustomerInvoice(invoice)}
                                        sx={{
                                            color: COLORS.blue,
                                            background: COLORS.blueLight,
                                            border: "1px solid #B2DDFF",
                                        }}
                                    >
                                        <Visibility sx={{ fontSize: "17px !important" }} />
                                    </IconButton></span>
                                </Tooltip>

                                <Tooltip title={financeText("Edit customer invoice")} arrow>
                                    <span style={{ display: "inline-flex" }}><IconButton
                                        size="small"
                                        disabled={!invoiceId}
                                        onClick={() => openCustomerInvoice(invoice, true)}
                                        sx={{
                                            color: COLORS.amber,
                                            background: COLORS.amberLight,
                                            border: "1px solid #FEDF89",
                                        }}
                                    >
                                        <Edit sx={{ fontSize: "17px !important" }} />
                                    </IconButton></span>
                                </Tooltip>

                                <Tooltip title={financeText("Show return information")} arrow>
                                    <span style={{ display: "inline-flex" }}><MDButton
                                        size="small"
                                        variant={isSelected ? "contained" : "outlined"}
                                        color="dark"
                                        disabled={!invoiceId}
                                        onClick={() => selectInvoiceForReturns(invoice)}
                                        sx={{ borderRadius: "9px", minWidth: 74 }}
                                    >
                                        {financeText("Returns")}
                                    </MDButton></span>
                                </Tooltip>
                            </MDBox>
                        );
                    },
                },
            ],
            rows: customerInvoices,
        }),
        [
            customerInvoices,
            currency,
            financeJobId,
            job,
            navigate,
            selectedInvoiceId,
            financeLanguage,
        ],
    );

    const supplierCostTable = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("SUPPLIER"),
                    accessor: "supplierName",
                    Cell: ({ row }) => {
                        const name =
                            row.original.supplierName || row.original.supplier_name || "N/A";

                        return (
                            <MDBox display="flex" alignItems="center" gap={1}>
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: COLORS.navy,
                                        fontSize: "11px",
                                        fontWeight: 800,
                                    }}
                                >
                                    {name.slice(0, 2).toUpperCase()}
                                </Avatar>
                                <MDTypography
                                    variant="button"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {name}
                                </MDTypography>
                            </MDBox>
                        );
                    },
                },
                { Header: financeText("SERVICE"), accessor: "serviceType" },
                {
                    Header: financeText("EXPECTED"),
                    accessor: "expectedAmount",
                    Cell: ({ row }) => (
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.amber }}
                        >
                            {formatCompact(
                                row.original.expectedAmount || row.original.expected_amount,
                                row.original.currency || currency,
                            )}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("ACTUAL"),
                    accessor: "actualInvoiceAmount",
                    Cell: ({ row }) => (
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.red }}
                        >
                            {formatCompact(
                                row.original.actualInvoiceAmount ||
                                row.original.actual_invoice_amount,
                                row.original.currency || currency,
                            )}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("VARIANCE"),
                    accessor: "varianceAmount",
                    Cell: ({ row }) => {
                        const value = Number(
                            row.original.varianceAmount || row.original.variance_amount || 0,
                        );

                        return (
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{
                                    color: value > 0 ? COLORS.red : COLORS.green,
                                }}
                            >
                                {formatCompact(value, row.original.currency || currency)}
                            </MDTypography>
                        );
                    },
                },
                {
                    Header: financeText("TOLERANCE"),
                    accessor: "toleranceStatus",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={
                                row.original.toleranceStatus ||
                                row.original.tolerance_status ||
                                "Within Tolerance"
                            }
                        />
                    ),
                },
                {
                    Header: financeText("APPROVAL"),
                    accessor: "approvalStatus",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={
                                row.original.approvalStatus ||
                                row.original.approval_status ||
                                "Pending"
                            }
                        />
                    ),
                },
                {
                    Header: financeText("PAYMENT"),
                    accessor: "paymentStatus",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={
                                row.original.paymentStatus ||
                                row.original.payment_status ||
                                "Not Due"
                            }
                        />
                    ),
                },
            ],
            rows: supplierCosts,
        }),
        [supplierCosts, currency, financeLanguage],
    );

    const supplierInvoiceTable = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("INVOICE NO"),
                    accessor: "invoiceNo",
                    Cell: ({ row }) => (
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.navy }}
                        >
                            {row.original.invoiceNo || row.original.invoice_no || "N/A"}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("SUPPLIER"),
                    accessor: "supplierName",
                },
                {
                    Header: financeText("DATE"),
                    accessor: "invoiceDate",
                    Cell: ({ row }) =>
                        formatDate(row.original.invoiceDate || row.original.invoice_date),
                },
                {
                    Header: financeText("DUE DATE"),
                    accessor: "dueDate",
                    Cell: ({ row }) =>
                        formatDate(row.original.dueDate || row.original.due_date),
                },
                {
                    Header: financeText("AMOUNT"),
                    accessor: "totalAmount",
                    Cell: ({ row }) => (
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.red }}
                        >
                            {formatCompact(
                                row.original.totalAmount || row.original.total_amount,
                                row.original.currency || currency,
                            )}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("APPROVAL"),
                    accessor: "approvalStatus",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={
                                row.original.approvalStatus ||
                                row.original.approval_status ||
                                "Pending"
                            }
                        />
                    ),
                },
                {
                    Header: financeText("PAYMENT"),
                    accessor: "paymentStatus",
                    Cell: ({ row }) => (
                        <FinanceStatusChip
                            status={
                                row.original.paymentStatus ||
                                row.original.payment_status ||
                                "Not Due"
                            }
                        />
                    ),
                },
            ],
            rows: supplierInvoices,
        }),
        [supplierInvoices, currency, financeLanguage],
    );

    const documentTable = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("DOCUMENT"),
                    accessor: "name",
                    Cell: ({ row }) => (
                        <MDBox display="flex" alignItems="center" gap={1}>
                            <Description sx={{ color: COLORS.blue, fontSize: 18 }} />
                            <MDTypography
                                variant="button"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {row.original.name || financeText("Document")}
                            </MDTypography>
                        </MDBox>
                    ),
                },
                { Header: financeText("TYPE"), accessor: "type" },
                {
                    Header: financeText("UPLOADED AT"),
                    accessor: "createdAt",
                    Cell: ({ row }) =>
                        formatDate(row.original.createdAt || row.original.created_at),
                },
                {
                    Header: financeText("ACTION"),
                    accessor: "action",
                    Cell: ({ row }) =>
                        row.original.url ? (
                            <MDButton
                                size="small"
                                variant="outlined"
                                color="dark"
                                onClick={() =>
                                    window.open(row.original.url, "_blank", "noopener,noreferrer")
                                }
                                sx={{ borderRadius: "10px" }}
                            >
                                {financeText("View")}
                            </MDButton>
                        ) : (
                            <Chip
                                size="small"
                                label={financeText("No File")}
                                sx={{
                                    color: COLORS.subtext,
                                    background: "#F2F4F7",
                                    fontWeight: 700,
                                }}
                            />
                        ),
                },
            ],
            rows: documents,
        }),
        [documents, financeLanguage],
    );

    const linkedReturnInvoices = useMemo(() => {
        const candidates = [
            fetchedReturnInvoices,
            returnableInvoice?.linkedReturnInvoices,
            returnableInvoice?.returnInvoices,
            returnableInvoice?.return_invoices,
            returnableInvoice?.previousReturns,
            returnableInvoice?.previous_returns,
            originalInvoice?.linkedReturnInvoices,
            originalInvoice?.returnInvoices,
        ];

        return candidates.find(Array.isArray) || [];
    }, [fetchedReturnInvoices, originalInvoice, returnableInvoice]);

    const returnedAmount = firstDefinedNumber(
        returnableInvoice?.returnedAmount,
        returnableInvoice?.returned_amount,
        returnableInvoice?.totals?.returnedAmount,
        returnableInvoice?.returnSummary?.returnedAmount,
        returnableInvoice?.reservedReturnAmount,
        originalInvoice?.returnedAmount,
        originalInvoice?.returned_amount,
    );

    const originalInvoiceTotal = firstDefinedNumber(
        returnableInvoice?.originalAmount,
        returnableInvoice?.originalInvoiceTotal,
        returnableInvoice?.original_amount,
        returnableInvoice?.totalAmount,
        returnableInvoice?.invoiceSalesAmount,
        originalInvoice?.totalAmount,
        originalInvoice?.invoiceSalesAmount,
    );

    const returnableAmount = Math.max(
        firstDefinedNumber(
            returnableInvoice?.returnableAmount,
            returnableInvoice?.remainingReturnableAmount,
            returnableInvoice?.remaining_returnable_amount,
            returnableInvoice?.totals?.returnableAmount,
            returnableInvoice?.returnSummary?.returnableAmount,
            originalInvoiceTotal - returnedAmount,
        ),
        0,
    );

    const isFullyReturned =
        Boolean(
            returnableInvoice?.isFullyReturned ||
            returnableInvoice?.is_fully_returned,
        ) ||
        (originalInvoiceTotal > 0 && returnableAmount <= 0.009);

    const returnInvoiceTable = useMemo(
        () => ({
            columns: [
                {
                    Header: financeText("RETURN NO"),
                    accessor: "returnInvoiceNumber",
                    Cell: ({ row }) => (
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.navy }}
                        >
                            {row.original.returnInvoiceNumber ||
                                row.original.invoiceNumber ||
                                row.original.invoiceNo ||
                                "Draft"}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("TYPE"),
                    accessor: "returnType",
                    Cell: ({ row }) =>
                        String(row.original.returnType || "partial").toLowerCase() ===
                        "full"
                            ? financeText("Full Return")
                            : financeText("Partial Return"),
                },
                {
                    Header: financeText("DATE"),
                    accessor: "createdAt",
                    Cell: ({ row }) =>
                        formatDate(row.original.issuedAt || row.original.createdAt),
                },
                {
                    Header: financeText("AMOUNT"),
                    accessor: "totalAmount",
                    Cell: ({ row }) => (
                        <MDTypography
                            variant="button"
                            fontWeight="bold"
                            sx={{ color: COLORS.red }}
                        >
                            {formatCompact(
                                row.original.totalAmount ||
                                row.original.invoiceSalesAmount ||
                                row.original.returnedAmount,
                                row.original.currency ||
                                row.original.invoiceCurrency ||
                                currency,
                            )}
                        </MDTypography>
                    ),
                },
                {
                    Header: financeText("STATUS"),
                    accessor: "returnStatus",
                    Cell: ({ row }) => (
                        <FinanceStatusChip status={row.original.returnStatus || "Draft"} />
                    ),
                },
                {
                    Header: financeText("ACTION"),
                    accessor: "action",
                    Cell: ({ row }) => {
                        const returnInvoiceId = row.original._id || row.original.id;

                        return (
                            <MDButton
                                size="small"
                                variant="outlined"
                                color="dark"
                                disabled={!returnInvoiceId}
                                onClick={() =>
                                    navigate(
                                        `/invoice/finance/return-invoices/${returnInvoiceId}`,
                                        { state: { returnInvoice: row.original } },
                                    )
                                }
                                sx={{ borderRadius: "10px" }}
                            >
                                {financeText("View")}
                            </MDButton>
                        );
                    },
                },
            ],
            rows: linkedReturnInvoices,
        }),
        [currency, linkedReturnInvoices, navigate, financeLanguage],
    );

    const openCreateReturnInvoice = () => {
        if (!originalInvoiceId || isFullyReturned) return;

        navigate(
            `/invoice/finance/return-invoices/new?originalInvoiceId=${originalInvoiceId}`,
            {
                state: {
                    originalInvoice,
                    originalInvoiceId,
                    returnableInvoice,
                },
            },
        );
    };

    const openCreateInvoice = () => {
        const manualInvoiceId = getDedicatedManualInvoiceId(job);
        const isManualInvoice =
            getInvoiceOrigin(job) === "manual" && Boolean(manualInvoiceId);

        if (isManualInvoice) {
            navigate(`/invoice/finance/manual-invoices/${manualInvoiceId}/edit`, {
                state: {
                    invoice: job,
                    invoiceId: manualInvoiceId,
                    editMode: true,
                },
            });

            return;
        }

        // Normal invoice için job/invoice _id değil, gerçek shipment ID kullanılmalı.
        const shipmentIdCandidate =
            job?.shipmentId?._id ||
            job?.shipmentId ||
            job?.shipment_id?._id ||
            job?.shipment_id ||
            job?.shipment?._id ||
            job?.shipment ||
            job?.shipmentObjectId ||
            job?.shipment_object_id;

        // A plain truthy check isn't enough here: when none of the fields
        // above have real data, this chain can resolve to a non-empty but
        // useless value (e.g. the literal string "0" from a job whose own
        // underlying invoice.shipmentId was never set) -- "0" is truthy in
        // JS, so `!shipmentId` doesn't catch it. That silently created a
        // real, EDM-sent invoice (OHFL00599) with shipmentId "0", which
        // meant no real TMS shipment could ever be resolved for it -- its
        // "Not:" shipment-detail lines were permanently blank on EDM.
        // Requiring a real 24-hex-char Mongo ObjectId shape instead blocks
        // that case with the same clear error up front.
        const shipmentId =
            typeof shipmentIdCandidate === "string" && /^[0-9a-fA-F]{24}$/.test(shipmentIdCandidate)
                ? shipmentIdCandidate
                : null;

        if (!shipmentId) {
            appAlert(financeText("Shipment ID not found for this finance job!"));
            return;
        }

        const sourceItems =
            [
                job?.invoiceItems,
                job?.invoice_items,
                job?.cargoDetails,
                job?.cargo_details,
            ].find(Array.isArray) || [];

        const invoiceItems = sourceItems.map((item) => {
            const unitPrice = firstDefinedNumber(
                item?.unitPrice,
                item?.unit_price,
                item?.sellingRate,
                item?.selling_rate,
                item?.salesAmount,
                item?.sales_amount,
            );

            const unitQuantity =
                firstDefinedNumber(
                    item?.unitQuantity,
                    item?.unit_quantity,
                    item?.quantity,
                    item?.qty,
                ) || 1;

            return {
                ...item,
                commodity: item?.commodity || item?.invoiceItem || "",
                description: item?.description || "",
                totalCost: firstDefinedNumber(
                    item?.totalCost,
                    item?.total_cost,
                    item?.cost,
                    item?.unitCost,
                    item?.unit_cost,
                ),
                costCurrency: normalizeCurrencyCode(
                    item?.costCurrency || item?.cost_currency || costCurrency,
                ),
                unitPrice,
                sellingRate: unitPrice,
                sellingRateCurrency: normalizeCurrencyCode(
                    item?.sellingRateCurrency ||
                    item?.selling_rate_currency ||
                    item?.currency ||
                    currency,
                ),
                unitQuantity,
                amount:
                    firstDefinedNumber(item?.amount, item?.totalAmount) ||
                    unitPrice * unitQuantity,
            };
        });

        navigate("/create-invoice", {
            state: {
                openInvoiceModal: true,
                financeJobId: financeJobId || job?.financeJobId || job?.finance_job_id,
                shipments: [
                    {
                        ...job,
                        _id: shipmentId,
                        shipmentId,
                        shipmentNo: String(
                            job?.shipmentNo ||
                            job?.shipment_no ||
                            job?.jobNo ||
                            job?.job_no ||
                            "",
                        )
                            .replace("OHFL", "")
                            .replace("RHFL", "")
                            .replace("AHFL", ""),

                        customerName: getValue(
                            job,
                            "customerName",
                            "customer_name",
                            "N/A",
                        ),
                        customer: getValue(
                            job,
                            "customerName",
                            "customer_name",
                            "N/A",
                        ),

                        invoiceItems,
                        cargoDetails: invoiceItems,

                        sellingRateCurrency: currency,
                        costCurrency,
                        totalCostCurrency: costCurrency,
                        currency,

                        invoiceSalesAmount: revenue,
                        salesAmount: revenue,
                        totalAmount: revenue,
                        customerInvoiceAmount: revenue,
                        actualRevenueTotal: revenue,
                        expectedRevenueTotal: expectedRevenue,

                        operationalCost: rawOperationalCost,
                        buyingRate: rawBuyingRate,
                        actualCostTotal: rawCost,
                        expectedCostTotal: rawExpectedCost,
                        totalCost: rawCost,
                        totalCostIncludesBuyingRate: true,

                        actualProfit: profit ?? 0,
                        expectedProfit: expectedProfit ?? 0,
                        actualMargin: margin ?? 0,

                        sourceType: getSourceType(job),
                        invoiceOrigin: "shipment",
                    },
                ],
            },
        });
    };
    if (loading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox
                    minHeight="70vh"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    gap={2}
                >
                    <CircularProgress />
                    <MDTypography variant="button" sx={{ color: COLORS.subtext }}>
                        {financeText("Loading finance job details...")}
                    </MDTypography>
                </MDBox>
                <Footer />
            </DashboardLayout>
        );
    }

    if (!job) {
        return (
            <DashboardLayout>
                <DashboardNavbar />

                <MDBox
                    pt={6}
                    pb={3}
                    sx={{
                        minHeight: "100vh",
                        background: COLORS.page,
                        mx: { xs: -2, md: -3 },
                        px: { xs: 2, md: 3 },
                    }}
                >
                    <Card sx={{ ...cardSx, maxWidth: 640, mx: "auto" }}>
                        <MDBox p={5} textAlign="center">
                            <MDBox
                                width={64}
                                height={64}
                                mx="auto"
                                mb={2}
                                borderRadius="10px"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                sx={{
                                    background: COLORS.redLight,
                                    color: COLORS.red,
                                }}
                            >
                                <WarningAmber />
                            </MDBox>

                            <MDTypography
                                variant="h5"
                                fontWeight="bold"
                                sx={{ color: COLORS.text }}
                            >
                                {financeText("Finance job not found")}
                            </MDTypography>

                            <MDTypography
                                variant="body2"
                                mt={1}
                                sx={{ color: COLORS.subtext }}
                            >
                                {detailError ||
                                    financeText("The requested finance record may have been removed or is not accessible.")}
                            </MDTypography>

                            <MDButton
                                sx={{ mt: 2.5, borderRadius: "10px" }}
                                variant="contained"
                                onClick={() => navigate("/invoice/finance/jobs")}
                            >
                                {financeText("Back to Finance Jobs")}
                            </MDButton>
                        </MDBox>
                    </Card>
                </MDBox>

                <Footer />
            </DashboardLayout>
        );
    }

    const jobNo =
        getValue(job, "jobNo", "job_no") ||
        job?.shipmentNo ||
        getValue(job, "invoiceNo", "invoice_no") ||
        "Finance Job";

    const customerName = getValue(job, "customerName", "customer_name", "N/A");

    const source =
        getInvoiceOrigin(job) === "manual"
            ? "Manual"
            : getValue(job, "tmsSource", "tms_source", "TMS");
    const service = isManualInvoice
        ? "Manual Invoice"
        : getValue(job, "serviceType", "service_type", "-");

    const totalInvoiced = customerInvoices.reduce(
        (sum, item) =>
            sum +
            (convertCurrency(
                item?.totalAmount || item?.total_amount || 0,
                item?.currency || currency,
                currency,
            ) ?? 0),
        0,
    );

    const totalCollected = customerInvoices.reduce(
        (sum, item) =>
            sum +
            (convertCurrency(
                item?.paidAmount || item?.paid_amount || 0,
                item?.currency || currency,
                currency,
            ) ?? 0),
        0,
    );

    const receivable = Math.max(totalInvoiced - totalCollected, 0);

    const payable = supplierInvoices.reduce(
        (sum, item) =>
            sum +
            (convertCurrency(
                item?.netPayableAmount ||
                item?.net_payable_amount ||
                item?.totalAmount ||
                item?.total_amount ||
                0,
                item?.currency || costCurrency,
                currency,
            ) ?? 0),
        0,
    );

    // Sum the actual paidAmount field across every supplier invoice instead
    // of gating on a paymentStatus string: `.includes("paid")` also matched
    // "Unpaid" and "Partially Paid", so nearly everything counted as fully
    // paid, and even an exact "Paid" match would have used the invoice's
    // full total rather than what was actually paid on a partial payment.
    const paidSupplierAmount = supplierInvoices.reduce(
        (sum, item) =>
            sum +
            (convertCurrency(
                item?.paidAmount || item?.paid_amount || 0,
                item?.currency || costCurrency,
                currency,
            ) ?? 0),
        0,
    );

    const openPayable = Math.max(payable - paidSupplierAmount, 0);
    const cashPosition = totalCollected - paidSupplierAmount;
    const financeHealth = Math.min(
        100,
        Math.max(
            0,
            Math.round(
                40 +
                Math.max(margin, 0) * 1.2 +
                collectionRate * 0.35 -
                (receivable > 0 ? 8 : 0) -
                (openPayable > totalCollected ? 10 : 0),
            ),
        ),
    );

    const chartData = [
        { label: "Expected Revenue", value: expectedRevenue, color: COLORS.blue },
        { label: "Actual Revenue", value: revenue, color: "#0BA5EC" },
        { label: "Operational Cost", value: operationalCost, color: COLORS.red },
        { label: "Buying Rate", value: buyingRate, color: COLORS.amber },
        { label: "Actual Cost", value: cost, color: COLORS.purple },
        {
            label: "Net Profit",
            value: profit,
            color: profit >= 0 ? COLORS.green : COLORS.red,
        },
    ];

    const maxChartValue = Math.max(
        ...chartData.map((item) => Math.abs(item.value)),
        1,
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />

            <MDBox
                pt={6}
                pb={3}
                sx={{
                    minHeight: "100vh",
                    background: COLORS.page,
                    mx: { xs: -2, md: -3 },
                    px: { xs: 2, md: 3 },
                }}
            >
                <Card
                    sx={{
                        borderRadius: "22px",
                        color: COLORS.white,
                        background:
                            "linear-gradient(125deg, #081A31 0%, #0D2D52 52%, #175CD3 135%)",
                        boxShadow: "0 18px 46px rgba(11, 31, 58, 0.22)",
                        overflow: "hidden",
                        position: "relative",
                        mb: 3,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            width: 360,
                            height: 360,
                            borderRadius: "50%",
                            right: -130,
                            top: -180,
                            background: "rgba(255,255,255,.07)",
                        },
                        "&::before": {
                            content: '""',
                            position: "absolute",
                            width: 230,
                            height: 230,
                            borderRadius: "50%",
                            right: 120,
                            bottom: -170,
                            background: "rgba(11,165,236,.16)",
                        },
                    }}
                >
                    <MDBox p={{ xs: 2.5, md: 3.5 }} position="relative" zIndex={1}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} lg={7}>
                                <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                    <Tooltip title={financeText("Back to Finance Jobs")}>
                                        <IconButton
                                            onClick={() => navigate("/invoice/finance/jobs")}
                                            sx={{
                                                color: "#FFFFFF",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.14)",
                                                "&:hover": {
                                                    background: "rgba(255,255,255,.16)",
                                                },
                                            }}
                                        >
                                            <ArrowBack />
                                        </IconButton>
                                    </Tooltip>

                                    <MDBox
                                        width={34}
                                        height={34}
                                        borderRadius="10px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{ background: "rgba(255,255,255,.12)" }}
                                    >
                                        <WorkspacePremium sx={{ color: "#FFFFFF", fontSize: 20 }} />
                                    </MDBox>

                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color: "rgba(255,255,255,.72)",
                                            letterSpacing: ".12em",
                                        }}
                                    >
                                        {financeText("FINANCE JOB CONTROL CENTER")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h2"
                                    fontWeight="bold"
                                    sx={{ color: "#FFFFFF", lineHeight: 1.12 }}
                                >
                                    {jobNo}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color: "rgba(255,255,255,.72)",
                                        maxWidth: 720,
                                    }}
                                >
                                    {source} · {service} · {customerName}
                                </MDTypography>

                                <MDBox display="flex" flexWrap="wrap" gap={1} mt={2.5}>
                                    <FinanceStatusChip
                                        status={
                                            isManualInvoice
                                                ? getValue(
                                                    job,
                                                    "invoiceStatus",
                                                    "invoice_status",
                                                    "Draft",
                                                )
                                                : getValue(
                                                    job,
                                                    "financeStatus",
                                                    "finance_status",
                                                    "Finance Open",
                                                )
                                        }
                                    />

                                    <FinanceStatusChip
                                        status={
                                            isManualInvoice
                                                ? getValue(
                                                    job,
                                                    "approvalStatus",
                                                    "approval_status",
                                                    "Pending",
                                                )
                                                : getValue(
                                                    job,
                                                    "financeClosingStatus",
                                                    "finance_closing_status",
                                                    "Not Ready",
                                                )
                                        }
                                    />

                                    <Chip
                                        icon={
                                            <CalendarMonth sx={{ color: "#B2DDFF !important" }} />
                                        }
                                        label={`${financeText("Updated")} ${new Date().toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}`}
                                        sx={{
                                            color: "#FFFFFF",
                                            background: "rgba(255,255,255,.10)",
                                            border: "1px solid rgba(255,255,255,.14)",
                                        }}
                                    />
                                </MDBox>
                            </Grid>

                            <Grid item xs={12} lg={5}>
                                <MDBox
                                    p={2}
                                    borderRadius="16px"
                                    sx={{
                                        background: "rgba(255,255,255,.09)",
                                        border: "1px solid rgba(255,255,255,.13)",
                                        backdropFilter: "blur(8px)",
                                    }}
                                >
                                    <MDBox
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        mb={1}
                                    >
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{ color: "rgba(255,255,255,.72)" }}
                                        >
                                            NET JOB PROFIT
                                        </MDTypography>

                                        <Paid sx={{ color: "#75E0A7" }} />
                                    </MDBox>

                                    <MDTypography
                                        variant="h3"
                                        fontWeight="bold"
                                        sx={{
                                            color: profit >= 0 ? "#FFFFFF" : "#FECDCA",
                                        }}
                                    >
                                        {ratesAvailable
                                            ? formatDisplayCompact(profit, currency)
                                            : financeText("Rate unavailable")}
                                    </MDTypography>

                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: "rgba(255,255,255,.68)" }}
                                    >
                                        {financeText("Margin")} {ratesAvailable ? formatPercent(margin) : "N/A"} · {financeText("Collection")} {collectionRate.toFixed(1)}%
                                    </MDTypography>

                                    {/*
                                      * Display-currency selector: re-renders
                                      * every aggregated figure on this page in
                                      * the chosen currency. It does not filter
                                      * anything and does not change per-invoice
                                      * row amounts.
                                      *
                                      * The outer white box (not just the input
                                      * itself) matters here: this sits on the
                                      * dark gradient hero card, and a shrunk
                                      * label floats slightly ABOVE the input's
                                      * own border. With only the input boxed
                                      * in white, that sliver of label sat
                                      * directly on the dark background with
                                      * its default dark text color, reading as
                                      * a garbled, half-cut-off mess. Padding
                                      * the label's float zone with the same
                                      * white background fixes it.
                                      */}
                                    <MDBox
                                        mt={2}
                                        px={0.5}
                                        pt={1.25}
                                        pb={0.5}
                                        borderRadius="11px"
                                        sx={{ background: "#FFFFFF" }}
                                    >
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            label={financeText("Display Currency")}
                                            value={selectedCurrency}
                                            onChange={(event) =>
                                                setSelectedCurrency(event.target.value)
                                            }
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    borderRadius: "11px",
                                                    background: "#FFFFFF",
                                                },
                                            }}
                                        >
                                            {CURRENCY_OPTIONS.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </MDBox>

                                    <MDBox display="flex" gap={1} mt={2} alignItems="stretch">
                                        <MDBox
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                p: "4px",
                                                background: "rgba(255,255,255,.10)",
                                                border: "1px solid rgba(255,255,255,.20)",
                                                borderRadius: "12px",
                                                boxShadow: "inset 0 1px 2px rgba(0,0,0,.10)",
                                                flexShrink: 0,
                                            }}
                                        >
                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: { value: "en" },
                                                    })
                                                }
                                                title="English"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "7px",
                                                    height: 38,
                                                    minWidth: 76,
                                                    px: 1.15,
                                                    borderRadius: "8px",
                                                    background:
                                                        financeLanguage === "en"
                                                            ? "#FFFFFF"
                                                            : "transparent",
                                                    color:
                                                        financeLanguage === "en"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",
                                                    boxShadow:
                                                        financeLanguage === "en"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",
                                                    transition: "all .18s ease",
                                                    "&:hover": {
                                                        background:
                                                            financeLanguage === "en"
                                                                ? "#FFFFFF"
                                                                : "rgba(255,255,255,.12)",
                                                    },
                                                }}
                                            >
                                                <MDBox
                                                    component="img"
                                                    src="/flags/gb.svg"
                                                    alt="English"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />
                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    EN
                                                </MDTypography>
                                            </MDBox>

                                            <MDBox
                                                component="button"
                                                type="button"
                                                onClick={() =>
                                                    changeFinanceLanguage({
                                                        target: { value: "tr" },
                                                    })
                                                }
                                                title="Türkçe"
                                                sx={{
                                                    appearance: "none",
                                                    WebkitAppearance: "none",
                                                    border: "none",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    gap: "7px",
                                                    height: 38,
                                                    minWidth: 76,
                                                    px: 1.15,
                                                    borderRadius: "8px",
                                                    background:
                                                        financeLanguage === "tr"
                                                            ? "#FFFFFF"
                                                            : "transparent",
                                                    color:
                                                        financeLanguage === "tr"
                                                            ? COLORS.navy
                                                            : "#FFFFFF",
                                                    boxShadow:
                                                        financeLanguage === "tr"
                                                            ? "0 3px 10px rgba(0,0,0,.18)"
                                                            : "none",
                                                    transition: "all .18s ease",
                                                    "&:hover": {
                                                        background:
                                                            financeLanguage === "tr"
                                                                ? "#FFFFFF"
                                                                : "rgba(255,255,255,.12)",
                                                    },
                                                }}
                                            >
                                                <MDBox
                                                    component="img"
                                                    src="/flags/tr.svg"
                                                    alt="Türkçe"
                                                    sx={{
                                                        width: 24,
                                                        height: 17,
                                                        objectFit: "cover",
                                                        borderRadius: "3px",
                                                        display: "block",
                                                    }}
                                                />
                                                <MDTypography
                                                    component="span"
                                                    sx={{
                                                        fontSize: "12px",
                                                        fontWeight: 800,
                                                        color: "inherit",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    TR
                                                </MDTypography>
                                            </MDBox>
                                        </MDBox>

                                        <MDButton
                                            variant="contained"
                                            onClick={openCreateInvoice}
                                            sx={{
                                                background: "#FFFFFF !important",
                                                color: `${COLORS.navy} !important`,
                                                flex: 1,
                                                boxShadow: "none",
                                            }}
                                        >
                                            <ReceiptLong sx={{ mr: 0.5 }} />
                                            {getDedicatedManualInvoiceId(job)
                                                ? financeText("Edit Manual Invoice")
                                                : financeText("Create Invoice")}
                                        </MDButton>

                                        <Tooltip title={financeText("View job draft PDF")}>
                                            <IconButton
                                                onClick={openJobDraftPdf}
                                                sx={{
                                                    color: "#FFFFFF",
                                                    border: "1px solid rgba(255,255,255,.18)",
                                                    borderRadius: "10px",
                                                }}
                                            >
                                                <PictureAsPdf />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title={financeText("Supplier Costs")}>
                                            <IconButton
                                                onClick={() =>
                                                    navigate("/invoice/finance/supplier-costs")
                                                }
                                                sx={{
                                                    color: "#FFFFFF",
                                                    border: "1px solid rgba(255,255,255,.18)",
                                                    borderRadius: "10px",
                                                }}
                                            >
                                                <AccountBalanceWallet />
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>

                <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title={financeText(
                                vatIncludedJob ? "Actual Revenue (incl. VAT)" : "Actual Revenue",
                            )}
                            value={formatDisplayCompact(billedRevenue, currency)}
                            subtitle={
                                vatIncludedJob
                                    ? `${financeText("Net")} ${formatDisplayExact(revenue, currency)} · ${financeText("Expected")} ${formatDisplayExact(expectedRevenue, currency)}`
                                    : `${financeText("Expected")} ${formatDisplayExact(expectedRevenue, currency)}`
                            }
                            icon={<ArrowUpward />}
                            tone="blue"
                            delta={revenueDelta}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title={financeText("Actual Cost")}
                            value={formatDisplayCompact(rawCost, costCurrency)}
                            subtitle={`${financeText("Expected")} ${formatDisplayExact(rawExpectedCost, costCurrency)}`}
                            icon={<ArrowDownward />}
                            tone="red"
                            delta={costDelta}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title={financeText("Actual Profit")}
                            value={
                                ratesAvailable
                                    ? formatDisplayCompact(profit, currency)
                                    : financeText("Rate unavailable")
                            }
                            subtitle={
                                ratesAvailable
                                    ? `${financeText("Expected")} ${formatDisplayExact(expectedProfit, currency)}`
                                    : `${financeText("Missing")} ${costCurrency}/${currency} ${financeText("exchange rate")}`
                            }
                            icon={<Paid />}
                            tone={!ratesAvailable ? "amber" : profit >= 0 ? "green" : "red"}
                            delta={profitDelta}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title={financeText("Margin")}
                            value={ratesAvailable ? formatPercent(margin) : "N/A"}
                            subtitle={`${financeText("Collection")} ${collectionRate.toFixed(1)}%`}
                            icon={<BusinessCenter />}
                            tone={margin < 10 ? "red" : margin < 20 ? "amber" : "purple"}
                            delta={margin - 20}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title={financeText("Operational Cost")}
                            value={formatDisplayCompact(rawOperationalCost, costCurrency)}
                            subtitle={financeText("Freight and operating expenses")}
                            icon={<ArrowDownward />}
                            tone="red"
                            delta={0}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title={financeText("Buying Rate")}
                            value={formatDisplayCompact(rawBuyingRate, costCurrency)}
                            subtitle={financeText("Additional purchase cost")}
                            icon={<AccountBalanceWallet />}
                            tone="amber"
                            delta={0}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title={financeText("Receivable")}
                            value={formatDisplayCompact(receivable, currency)}
                            subtitle={financeText("Outstanding customer balance")}
                            icon={<ReceiptLong />}
                            tone="amber"
                            delta={receivable > 0 ? -3.2 : 2.4}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title={financeText("Open Payable")}
                            value={formatDisplayCompact(openPayable, currency)}
                            subtitle={financeText("Unpaid supplier exposure")}
                            icon={<AccountBalanceWallet />}
                            tone="purple"
                            delta={openPayable > 0 ? -1.4 : 2.1}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title={financeText("Cash Position")}
                            value={formatDisplayCompact(cashPosition, currency)}
                            subtitle={financeText("Collections less supplier payments")}
                            icon={<Paid />}
                            tone={cashPosition >= 0 ? "green" : "red"}
                            delta={cashPosition >= 0 ? 6.8 : -6.8}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MetricCard
                            title={financeText("Finance Health")}
                            value={`${financeHealth}/100`}
                            subtitle={financeText("Profitability and liquidity score")}
                            icon={<WorkspacePremium />}
                            tone={
                                financeHealth >= 75
                                    ? "green"
                                    : financeHealth >= 50
                                        ? "amber"
                                        : "red"
                            }
                            delta={financeHealth - 70}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} lg={3}>
                        <MDBox
                            height="100%"
                            sx={{ cursor: "pointer" }}
                            onClick={() =>
                                navigate("/invoice/finance/collections", {
                                    state: {
                                        job,
                                        invoiceId:
                                            job?.invoiceId || job?.invoice_id || job?._id || job?.id,
                                    },
                                })
                            }
                        >
                            <MetricCard
                                title={financeText("Credit Risk")}
                                value={riskOption.label}
                                subtitle={
                                    collectionRisk.reason ||
                                    (collectionRisk.provisionAmount > 0
                                        ? `${financeText("Provision")} ${formatDisplayExact(
                                            collectionRisk.provisionAmount,
                                            currency,
                                        )}`
                                        : financeText("Managed in Collections"))
                                }
                                icon={<WarningAmber />}
                                tone={riskTone}
                                delta={0}
                            />
                        </MDBox>
                    </Grid>
                </Grid>

                <Grid container spacing={2.5} mt={0.25}>
                    <Grid item xs={12} xl={8}>
                        <Card sx={{ ...cardSx, height: "100%" }}>
                            <MDBox p={3}>
                                <MDBox
                                    display="flex"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1}
                                    mb={3}
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {financeText("Financial Performance")}
                                        </MDTypography>
                                        <MDTypography
                                            variant="caption"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Expected and actual revenue, cost and profit comparison.")}
                                        </MDTypography>
                                    </MDBox>

                                    <Chip
                                        size="small"
                                        label={`${financeText("Margin")} ${formatPercent(margin)}`}
                                        sx={{
                                            color:
                                                margin >= 20
                                                    ? COLORS.green
                                                    : margin >= 10
                                                        ? COLORS.amber
                                                        : COLORS.red,
                                            background:
                                                margin >= 20
                                                    ? COLORS.greenLight
                                                    : margin >= 10
                                                        ? COLORS.amberLight
                                                        : COLORS.redLight,
                                            fontWeight: 800,
                                        }}
                                    />
                                </MDBox>

                                <MDBox display="flex" flexDirection="column" gap={2}>
                                    {chartData.map((item) => {
                                        const width = Math.min(
                                            (Math.abs(item.value) / maxChartValue) * 100,
                                            100,
                                        );

                                        return (
                                            <MDBox key={financeText(item.label)}>
                                                <MDBox
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="space-between"
                                                    mb={0.7}
                                                >
                                                    <MDTypography
                                                        variant="button"
                                                        fontWeight="bold"
                                                        sx={{ color: COLORS.text }}
                                                    >
                                                        {financeText(item.label)}
                                                    </MDTypography>

                                                    <MDTypography
                                                        variant="button"
                                                        fontWeight="bold"
                                                        sx={{ color: item.color }}
                                                    >
                                                        {formatDisplayCompact(item.value, currency)}
                                                    </MDTypography>
                                                </MDBox>

                                                <MDBox
                                                    height={12}
                                                    borderRadius="999px"
                                                    sx={{
                                                        background: "#F2F4F7",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <MDBox
                                                        height="100%"
                                                        width={`${width}%`}
                                                        borderRadius="999px"
                                                        sx={{
                                                            background: `linear-gradient(90deg, ${item.color}, ${item.color}CC)`,
                                                            transition: "width .45s ease",
                                                        }}
                                                    />
                                                </MDBox>
                                            </MDBox>
                                        );
                                    })}
                                </MDBox>
                            </MDBox>
                        </Card>
                    </Grid>

                    <Grid item xs={12} xl={4}>
                        <Card sx={{ ...cardSx, height: "100%" }}>
                            <MDBox p={3}>
                                <MDTypography
                                    variant="h6"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.text }}
                                >
                                    {financeText("Liquidity Overview")}
                                </MDTypography>

                                <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                    {financeText("Cash conversion and open exposure for this finance job.")}
                                </MDTypography>

                                <MDBox
                                    mt={3}
                                    width={180}
                                    height={180}
                                    mx="auto"
                                    borderRadius="50%"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                        background: `conic-gradient(${COLORS.green} ${
                                            Math.min(collectionRate, 100) * 3.6
                                        }deg, #EEF2F6 0deg)`,
                                        position: "relative",
                                    }}
                                >
                                    <MDBox
                                        width={138}
                                        height={138}
                                        borderRadius="50%"
                                        display="flex"
                                        flexDirection="column"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            background: "#FFFFFF",
                                            boxShadow: "inset 0 0 0 1px #E4E7EC",
                                        }}
                                    >
                                        <MDTypography
                                            variant="h3"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {collectionRate.toFixed(0)}%
                                        </MDTypography>
                                        <MDTypography
                                            variant="caption"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Collected")}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                <Divider sx={{ my: 2.5 }} />

                                {[
                                    ["Receivable", receivable, COLORS.amber],
                                    ["Open Payable", openPayable, COLORS.purple],
                                    [
                                        "Cash Position",
                                        cashPosition,
                                        cashPosition >= 0 ? COLORS.green : COLORS.red,
                                    ],
                                ].map(([label, amount, color]) => (
                                    <MDBox
                                        key={financeText(label)}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        py={1}
                                    >
                                        <MDBox display="flex" alignItems="center" gap={1}>
                                            <MDBox
                                                width={9}
                                                height={9}
                                                borderRadius="50%"
                                                sx={{ background: color }}
                                            />
                                            <MDTypography
                                                variant="button"
                                                sx={{ color: COLORS.subtext }}
                                            >
                                                {financeText(label)}
                                            </MDTypography>
                                        </MDBox>

                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{ color }}
                                        >
                                            {formatDisplayCompact(amount, currency)}
                                        </MDTypography>
                                    </MDBox>
                                ))}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>

                <Card
                    sx={{
                        ...cardSx,
                        mt: 2.5,
                        borderColor: "#D0D5DD",
                        background: "#FFFFFF",
                        boxShadow: "none",
                    }}
                >
                    <MDBox p={{ xs: 1.5, sm: 2, md: 2.5 }}>
                        <MDBox
                            display="flex"
                            alignItems={{ xs: "flex-start", md: "center" }}
                            justifyContent="space-between"
                            flexDirection={{ xs: "column", md: "row" }}
                            gap={1.25}
                            px={{ xs: 0.25, md: 0.5 }}
                            mb={1.75}
                        >
                            <MDBox>
                                <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{
                                        color: COLORS.blue,
                                        letterSpacing: ".1em",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {financeText("Finance Workspace")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={0.35}
                                    sx={{ color: COLORS.subtext }}
                                >
                                    {financeText("Manage billing, supplier exposure, documents and profitability from one operational workspace.")}
                                </MDTypography>
                            </MDBox>

                            <MDBox
                                display="flex"
                                alignItems="center"
                                flexWrap="wrap"
                                gap={0.75}
                            >
                                <Chip
                                    size="small"
                                    label={`${financeText("Section")} ${tab + 1} ${financeText("of")} ${financeTabs.length}`}
                                    sx={{
                                        height: 28,
                                        color: COLORS.subtext,
                                        background: "#F2F4F7",
                                        border: "1px solid #E4E7EC",
                                        fontWeight: 700,
                                    }}
                                />

                                <Chip
                                    size="small"
                                    label={financeText(activeFinanceTab.label)}
                                    sx={{
                                        height: 28,
                                        color: COLORS.blue,
                                        background: COLORS.blueLight,
                                        border: "1px solid #B2DDFF",
                                        fontWeight: 800,
                                    }}
                                />
                            </MDBox>
                        </MDBox>

                        <MDBox
                            sx={{
                                p: "6px",
                                borderRadius: "10px",
                                background: "#F5F7FA",
                                border: "1px solid #DDE2E8",
                                boxShadow: "inset 0 1px 2px rgba(16, 24, 40, 0.04)",
                            }}
                        >
                            <Tabs
                                value={tab}
                                onChange={(_, value) => setTab(value)}
                                variant="scrollable"
                                scrollButtons="auto"
                                allowScrollButtonsMobile
                                aria-label="Finance job detail sections"
                                sx={{
                                    minHeight: 60,
                                    overflow: "visible",
                                    "& .MuiTabs-scroller": {
                                        overflowX: "auto !important",
                                        scrollbarWidth: "thin",
                                        scrollbarColor: "#D0D5DD transparent",
                                        "&::-webkit-scrollbar": {
                                            height: 5,
                                        },
                                        "&::-webkit-scrollbar-thumb": {
                                            background: "#D0D5DD",
                                            borderRadius: "999px",
                                        },
                                    },
                                    "& .MuiTabs-flexContainer": {
                                        gap: "6px",
                                    },
                                    "& .MuiTabs-indicator": {
                                        display: "none",
                                    },
                                    "& .MuiTabs-scrollButtons": {
                                        width: 36,
                                        minWidth: 36,
                                        height: 44,
                                        my: "auto",
                                        mx: 0.25,
                                        color: COLORS.navy,
                                        background: "rgba(255,255,255,.82)",
                                        border: "1px solid #D0D5DD",
                                        borderRadius: "10px",
                                        transition: "all .2s ease",
                                        "&:hover": {
                                            color: COLORS.blue,
                                            borderColor: "#B2DDFF",
                                            background: "#FFFFFF",
                                        },
                                        "&.Mui-disabled": {
                                            opacity: 0.25,
                                        },
                                    },
                                    "& .MuiTab-root": {
                                        position: "relative",
                                        minHeight: 60,
                                        minWidth: { xs: 168, md: 182, xl: 0 },
                                        maxWidth: "none",
                                        flex: { xs: "0 0 auto", xl: "1 1 0" },
                                        px: 1.25,
                                        py: 0.75,
                                        textTransform: "none",
                                        color: COLORS.subtext,
                                        border: "1px solid transparent",
                                        borderRadius: "8px",
                                        transition:
                                            "color .2s ease, background .2s ease, border-color .2s ease, box-shadow .2s ease, transform .2s ease",
                                        "&:hover": {
                                            color: COLORS.navy,
                                            background: "rgba(255,255,255,.78)",
                                            borderColor: "rgba(208,213,221,.9)",
                                            transform: "none",
                                        },
                                        "&.Mui-selected": {
                                            color: `${COLORS.navy} !important`,
                                            background: "#FFFFFF",
                                            borderColor: "#B2DDFF",
                                            boxShadow:
                                                "0 4px 12px rgba(16, 24, 40, 0.08), 0 1px 2px rgba(23, 92, 211, 0.08)",
                                            transform: "none",
                                        },
                                        // Genel tema seçili sekme içeriğini beyaz yapar; burada seçili sekme beyaz zeminli
                                        "&.Mui-selected > *:not(.MuiTouchRipple-root), &.Mui-selected .MuiTypography-root": {
                                            color: `${COLORS.navy} !important`,
                                        },
                                        "&.Mui-selected::after": {
                                            content: '""',
                                            position: "absolute",
                                            left: 14,
                                            right: 14,
                                            bottom: -1,
                                            height: 3,
                                            borderRadius: "999px 999px 0 0",
                                            background: `linear-gradient(90deg, ${COLORS.blue}, #0BA5EC)`,
                                        },
                                        "&.Mui-selected .finance-tab-icon": {
                                            color: COLORS.blue,
                                            background: COLORS.blueLight,
                                            borderColor: "#B2DDFF",
                                            boxShadow: "0 2px 6px rgba(23, 92, 211, 0.08)",
                                        },
                                        "&.Mui-selected .finance-tab-count": {
                                            color: COLORS.blue,
                                            background: COLORS.blueLight,
                                            borderColor: "#B2DDFF",
                                        },
                                        "&.Mui-focusVisible": {
                                            outline: "3px solid rgba(23, 92, 211, 0.2)",
                                            outlineOffset: 2,
                                        },
                                    },
                                }}
                            >
                                {financeTabs.map((item, index) => (
                                    <Tab
                                        key={item.key}
                                        disableRipple
                                        aria-label={financeText(item.label)}
                                        id={`finance-tab-${index}`}
                                        label={
                                            <FinanceTabLabel
                                                icon={item.icon}
                                                label={financeText(item.label)}
                                                count={item.count}
                                            />
                                        }
                                    />
                                ))}
                            </Tabs>
                        </MDBox>

                        <MDBox
                            mt={1.5}
                            px={{ xs: 0.25, md: 0.5 }}
                            display="flex"
                            alignItems={{ xs: "flex-start", sm: "center" }}
                            justifyContent="space-between"
                            flexDirection={{ xs: "column", sm: "row" }}
                            gap={0.75}
                        >
                            <MDBox display="flex" alignItems="center" gap={0.8} minWidth={0}>
                                <MDBox
                                    width={8}
                                    height={8}
                                    borderRadius="50%"
                                    flexShrink={0}
                                    sx={{
                                        background: COLORS.blue,
                                        boxShadow: "0 0 0 4px rgba(23, 92, 211, 0.1)",
                                    }}
                                />
                                <MDTypography variant="caption" sx={{ color: COLORS.subtext }}>
                                    {financeText(activeFinanceTab.description)}
                                </MDTypography>
                            </MDBox>

                            {activeFinanceTab.count !== null &&
                            activeFinanceTab.count !== undefined ? (
                                <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{ color: COLORS.navy, whiteSpace: "nowrap" }}
                                >
                                    {activeFinanceTab.count === 1
                                        ? `1 ${financeText("record")}`
                                        : `${activeFinanceTab.count} ${financeText("records")}`}
                                </MDTypography>
                            ) : null}
                        </MDBox>

                        <Divider sx={{ my: { xs: 2, md: 2.5 } }} />

                        {tab === 0 && (
                            <Grid container spacing={2.5}>
                                <Grid item xs={12} lg={6}>
                                    <Card sx={{ ...cardSx, boxShadow: "none", height: "100%" }}>
                                        <MDBox p={3}>
                                            <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                                                <MDBox
                                                    width={38}
                                                    height={38}
                                                    borderRadius="11px"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    sx={{
                                                        color: COLORS.blue,
                                                        background: COLORS.blueLight,
                                                        border: "1px solid #B2DDFF",
                                                    }}
                                                >
                                                    <BusinessCenter sx={{ fontSize: 19 }} />
                                                </MDBox>

                                                <MDBox>
                                                    <MDTypography
                                                        variant="h6"
                                                        fontWeight="bold"
                                                        sx={{ color: COLORS.text }}
                                                    >
                                                        {financeText("Job Information")}
                                                    </MDTypography>

                                                    <MDTypography
                                                        variant="caption"
                                                        sx={{ color: COLORS.subtext }}
                                                    >
                                                        {financeText("Core shipment and commercial details.")}
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>

                                            {[
                                                [
                                                    financeText("Customer"),
                                                    getValue(job, "customerName", "customer_name", "N/A"),
                                                ],
                                                [financeText("Shipper"), getValue(job, "shipper", "shipper", "N/A")],
                                                [
                                                    financeText("Consignee"),
                                                    getValue(job, "consignee", "consignee", "N/A"),
                                                ],
                                                [financeText("Origin"), getValue(job, "origin", "origin", "N/A")],
                                                [
                                                    financeText("Destination"),
                                                    getValue(job, "destination", "destination", "N/A"),
                                                ],
                                                [
                                                    financeText("Incoterm"),
                                                    getValue(job, "incoterm", "incoterm", "N/A"),
                                                ],
                                            ].map(([label, value], index) => (
                                                <MDBox
                                                    key={financeText(label)}
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    py={1.35}
                                                    borderBottom={
                                                        index === 5 ? "none" : `1px solid ${COLORS.border}`
                                                    }
                                                    gap={2}
                                                >
                                                    <MDTypography
                                                        variant="button"
                                                        sx={{ color: COLORS.subtext }}
                                                    >
                                                        {financeText(label)}
                                                    </MDTypography>

                                                    <MDTypography
                                                        variant="button"
                                                        fontWeight="bold"
                                                        textAlign="right"
                                                        sx={{ color: COLORS.text }}
                                                    >
                                                        {value}
                                                    </MDTypography>
                                                </MDBox>
                                            ))}
                                        </MDBox>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} lg={6}>
                                    <Card sx={{ ...cardSx, boxShadow: "none", height: "100%" }}>
                                        <MDBox p={3}>
                                            <MDBox display="flex" alignItems="center" gap={1} mb={2}>
                                                <MDBox
                                                    width={38}
                                                    height={38}
                                                    borderRadius="11px"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    sx={{
                                                        color: COLORS.green,
                                                        background: COLORS.greenLight,
                                                        border: "1px solid #ABEFC6",
                                                    }}
                                                >
                                                    <CheckCircle sx={{ fontSize: 19 }} />
                                                </MDBox>

                                                <MDBox>
                                                    <MDTypography
                                                        variant="h6"
                                                        fontWeight="bold"
                                                        sx={{ color: COLORS.text }}
                                                    >
                                                        {financeText("Finance Status")}
                                                    </MDTypography>

                                                    <MDTypography
                                                        variant="caption"
                                                        sx={{ color: COLORS.subtext }}
                                                    >
                                                        {financeText("Current invoice, payment and closing position.")}
                                                    </MDTypography>
                                                </MDBox>
                                            </MDBox>

                                            {[
                                                [
                                                    financeText("Customer Invoice"),
                                                    getValue(
                                                        job,
                                                        "customerInvoiceStatus",
                                                        "customer_invoice_status",
                                                        "Not Issued",
                                                    ),
                                                ],
                                                [
                                                    financeText("Supplier Invoice"),
                                                    getValue(
                                                        job,
                                                        "supplierInvoiceStatus",
                                                        "supplier_invoice_status",
                                                        "Waiting",
                                                    ),
                                                ],
                                                [
                                                    financeText("Collection"),
                                                    getValue(
                                                        job,
                                                        "collectionStatus",
                                                        "collection_status",
                                                        "Pending",
                                                    ),
                                                ],
                                                [
                                                    financeText("Supplier Payment"),
                                                    getValue(
                                                        job,
                                                        "supplierPaymentStatus",
                                                        "supplier_payment_status",
                                                        "Not Due",
                                                    ),
                                                ],
                                                [
                                                    financeText("Closing"),
                                                    getValue(
                                                        job,
                                                        "financeClosingStatus",
                                                        "finance_closing_status",
                                                        "Not Ready",
                                                    ),
                                                ],
                                            ].map(([label, value], index) => (
                                                <MDBox
                                                    key={financeText(label)}
                                                    display="flex"
                                                    justifyContent="space-between"
                                                    alignItems="center"
                                                    py={1.35}
                                                    borderBottom={
                                                        index === 4 ? "none" : `1px solid ${COLORS.border}`
                                                    }
                                                    gap={2}
                                                >
                                                    <MDTypography
                                                        variant="button"
                                                        sx={{ color: COLORS.subtext }}
                                                    >
                                                        {financeText(label)}
                                                    </MDTypography>

                                                    <FinanceStatusChip status={value} />
                                                </MDBox>
                                            ))}

                                            <MDBox
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                pt={1.35}
                                                mt={0.25}
                                                borderTop={`1px solid ${COLORS.border}`}
                                                gap={2}
                                            >
                                                <MDTypography
                                                    variant="button"
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    Credit Risk
                                                </MDTypography>

                                                <Tooltip title={financeText("Open Collections")}>
                                                    <Chip
                                                        size="small"
                                                        label={financeText(riskOption.label)}
                                                        onClick={() =>
                                                            navigate("/invoice/finance/collections", {
                                                                state: {
                                                                    job,
                                                                    invoiceId:
                                                                        job?.invoiceId ||
                                                                        job?.invoice_id ||
                                                                        job?._id ||
                                                                        job?.id,
                                                                },
                                                            })
                                                        }
                                                        sx={{
                                                            color: riskOption.color,
                                                            background: riskOption.background,
                                                            border: `1px solid ${riskOption.border}`,
                                                            fontWeight: 800,
                                                            cursor: "pointer",
                                                        }}
                                                    />
                                                </Tooltip>
                                            </MDBox>
                                        </MDBox>
                                    </Card>
                                </Grid>
                            </Grid>
                        )}

                        {tab === 1 && (
                            <>
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {financeText("Customer Invoices")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Billing, payment and balance records for this job.")}
                                        </MDTypography>
                                    </MDBox>

                                    <MDBox display="flex" flexWrap="wrap" gap={1}>
                                        {originalInvoiceId && (
                                            <Tooltip
                                                title={
                                                    isFullyReturned
                                                        ? financeText("This invoice has been fully returned.")
                                                        : financeText("Create a full or partial return invoice.")
                                                }
                                            >
                        <span>
                          <MDButton
                              variant="outlined"
                              color="error"
                              disabled={
                                  loadingReturns ||
                                  isFullyReturned ||
                                  Boolean(returnError)
                              }
                              onClick={openCreateReturnInvoice}
                              sx={{ borderRadius: "10px" }}
                          >
                            {loadingReturns ? (
                                <CircularProgress
                                    size={16}
                                    color="inherit"
                                    sx={{ mr: 0.75 }}
                                />
                            ) : (
                                <AssignmentReturn sx={{ mr: 0.5 }} />
                            )}
                              {isFullyReturned
                                  ? financeText("Fully Returned")
                                  : financeText("Create Return Invoice")}
                          </MDButton>
                        </span>
                                            </Tooltip>
                                        )}

                                        <MDButton
                                            variant="contained"
                                            onClick={openCreateInvoice}
                                            sx={{
                                                borderRadius: "10px",
                                                background: `${COLORS.navy} !important`,
                                                color: "#FFFFFF !important",
                                            }}
                                        >
                                            <ReceiptLong sx={{ mr: 0.5 }} />
                                            {isManualInvoice
                                                ? financeText("Edit Manual Invoice")
                                                : financeText("Create Invoice")}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    flexWrap="wrap"
                                    gap={1.5}
                                    mb={2.5}
                                    px={2}
                                    py={1.5}
                                    borderRadius="8px"
                                    sx={{ background: COLORS.muted, border: `1px solid ${COLORS.border}` }}
                                >
                                    <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                                        <UploadFile sx={{ color: COLORS.subtext }} />
                                        <MDBox>
                                            <MDTypography variant="button" fontWeight="bold" sx={{ color: COLORS.text }}>
                                                {financeText("Supplier side")}: {supplierInvoices.length}{" "}
                                                {financeText("invoice(s)")}
                                            </MDTypography>
                                            <MDTypography variant="caption" display="block" sx={{ color: COLORS.subtext }}>
                                                {financeText("Payable")} {formatCompact(payable, currency)} ·{" "}
                                                {financeText("Paid")} {formatCompact(paidSupplierAmount, currency)} ·{" "}
                                                {financeText("Open")} {formatCompact(openPayable, currency)}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDButton variant="text" color="dark" size="small" onClick={() => setTab(3)}>
                                        {financeText("View Supplier Invoices")}
                                    </MDButton>
                                </MDBox>

                                {originalInvoiceId && (
                                    <Grid container spacing={2} mb={2.5}>
                                        <Grid item xs={12} md={4}>
                                            <Card sx={{ ...cardSx, boxShadow: "none" }}>
                                                <MDBox p={2}>
                                                    <MDTypography
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        sx={{ color: COLORS.subtext }}
                                                    >
                                                        ORIGINAL AMOUNT
                                                    </MDTypography>
                                                    <MDTypography
                                                        variant="h5"
                                                        fontWeight="bold"
                                                        mt={0.5}
                                                        sx={{ color: COLORS.text }}
                                                    >
                                                        {formatCompact(originalInvoiceTotal, currency)}
                                                    </MDTypography>
                                                </MDBox>
                                            </Card>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <Card sx={{ ...cardSx, boxShadow: "none" }}>
                                                <MDBox p={2}>
                                                    <MDTypography
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        sx={{ color: COLORS.subtext }}
                                                    >
                                                        ISSUED RETURNS
                                                    </MDTypography>
                                                    <MDTypography
                                                        variant="h5"
                                                        fontWeight="bold"
                                                        mt={0.5}
                                                        sx={{ color: COLORS.red }}
                                                    >
                                                        {formatCompact(returnedAmount, currency)}
                                                    </MDTypography>
                                                </MDBox>
                                            </Card>
                                        </Grid>

                                        <Grid item xs={12} md={4}>
                                            <Card sx={{ ...cardSx, boxShadow: "none" }}>
                                                <MDBox p={2}>
                                                    <MDTypography
                                                        variant="caption"
                                                        fontWeight="bold"
                                                        sx={{ color: COLORS.subtext }}
                                                    >
                                                        RETURNABLE AMOUNT
                                                    </MDTypography>
                                                    <MDTypography
                                                        variant="h5"
                                                        fontWeight="bold"
                                                        mt={0.5}
                                                        sx={{
                                                            color: isFullyReturned
                                                                ? COLORS.subtext
                                                                : COLORS.green,
                                                        }}
                                                    >
                                                        {loadingReturns
                                                            ? financeText("Loading...")
                                                            : formatCompact(returnableAmount, currency)}
                                                    </MDTypography>
                                                </MDBox>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                )}

                                {returnError && (
                                    <MDBox
                                        mb={2.5}
                                        px={2}
                                        py={1.5}
                                        borderRadius="8px"
                                        sx={{
                                            color: COLORS.red,
                                            background: COLORS.redLight,
                                            border: "1px solid #FECDCA",
                                        }}
                                    >
                                        <MDTypography
                                            variant="button"
                                            fontWeight="bold"
                                            sx={{ color: "inherit" }}
                                        >
                                            {returnError}
                                        </MDTypography>
                                    </MDBox>
                                )}

                                {customerInvoices.length ? (
                                    <MDBox sx={tableWrapperSx}>
                                        <DataTable
                                            table={customerInvoiceTable}
                                            canSearch
                                            entriesPerPage
                                            showTotalEntries
                                            isSorted
                                            noEndBorder
                                        />
                                    </MDBox>
                                ) : (
                                    <EmptyState
                                        title={financeText("No customer invoice found.")}
                                        icon={<ReceiptLong />}
                                    />
                                )}

                                <Divider sx={{ my: 3 }} />

                                <MDBox mb={2}>
                                    <MDTypography
                                        variant="h6"
                                        fontWeight="bold"
                                        sx={{ color: COLORS.text }}
                                    >
                                        {financeText("Return Invoice History")}
                                    </MDTypography>
                                    <MDTypography
                                        variant="caption"
                                        sx={{ color: COLORS.subtext }}
                                    >
                                        {financeText("Full and partial returns linked to the original invoice.")}
                                    </MDTypography>
                                </MDBox>

                                {linkedReturnInvoices.length ? (
                                    <MDBox sx={tableWrapperSx}>
                                        <DataTable
                                            table={returnInvoiceTable}
                                            canSearch
                                            entriesPerPage
                                            showTotalEntries
                                            isSorted
                                            noEndBorder
                                        />
                                    </MDBox>
                                ) : (
                                    <EmptyState
                                        title={financeText("No return invoice found.")}
                                        icon={<AssignmentReturn />}
                                    />
                                )}
                            </>
                        )}

                        {tab === 2 && (
                            <>
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {financeText("Supplier Costs")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Expected cost, actual invoice and variance control.")}
                                        </MDTypography>
                                    </MDBox>

                                    <MDButton
                                        variant="contained"
                                        onClick={() =>
                                            navigate("/invoice/finance/supplier-costs", {
                                                state: { financeJobId, job },
                                            })
                                        }
                                        sx={{
                                            borderRadius: "10px",
                                            background: `${COLORS.navy} !important`,
                                            color: "#FFFFFF !important",
                                        }}
                                    >
                                        <Paid sx={{ mr: 0.5 }} />
                                        {financeText("Add Cost Line")}
                                    </MDButton>
                                </MDBox>

                                {supplierCosts.length ? (
                                    <MDBox sx={tableWrapperSx}>
                                        <DataTable
                                            table={supplierCostTable}
                                            canSearch
                                            entriesPerPage
                                            showTotalEntries
                                            isSorted
                                            noEndBorder
                                        />
                                    </MDBox>
                                ) : (
                                    <EmptyState
                                        title={financeText("No supplier cost line found.")}
                                        icon={<Paid />}
                                    />
                                )}
                            </>
                        )}

                        {tab === 3 && (
                            <>
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {financeText("Supplier Invoices")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Supplier invoice approval and payment status.")}
                                        </MDTypography>
                                    </MDBox>

                                    <MDBox display="flex" gap={1.25} flexWrap="wrap">
                                        <MDButton
                                            variant="outlined"
                                            color="dark"
                                            onClick={openIncomingLinkDialog}
                                            sx={{ borderRadius: "10px" }}
                                        >
                                            <LinkIcon sx={{ mr: 0.5 }} />
                                            {financeText("Link Incoming Invoice")}
                                        </MDButton>

                                        <MDButton
                                            variant="contained"
                                            onClick={() =>
                                                navigate("/invoice/finance/supplier-invoices", {
                                                    state: { financeJobId, job },
                                                })
                                            }
                                            sx={{
                                                borderRadius: "10px",
                                                background: `${COLORS.navy} !important`,
                                                color: "#FFFFFF !important",
                                            }}
                                        >
                                            <UploadFile sx={{ mr: 0.5 }} />
                                            {financeText("Upload Invoice")}
                                        </MDButton>
                                    </MDBox>
                                </MDBox>

                                <MDBox
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    flexWrap="wrap"
                                    gap={1.5}
                                    mb={2.5}
                                    px={2}
                                    py={1.5}
                                    borderRadius="8px"
                                    sx={{ background: COLORS.muted, border: `1px solid ${COLORS.border}` }}
                                >
                                    <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                                        <ReceiptLong sx={{ color: COLORS.subtext }} />
                                        <MDBox>
                                            <MDTypography variant="button" fontWeight="bold" sx={{ color: COLORS.text }}>
                                                {financeText("Customer side")}: {customerInvoices.length}{" "}
                                                {financeText("invoice(s)")}
                                            </MDTypography>
                                            <MDTypography variant="caption" display="block" sx={{ color: COLORS.subtext }}>
                                                {financeText("Invoiced")} {formatCompact(totalInvoiced, currency)} ·{" "}
                                                {financeText("Collected")} {formatCompact(totalCollected, currency)} ·{" "}
                                                {financeText("Receivable")} {formatCompact(receivable, currency)}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                    <MDButton variant="text" color="dark" size="small" onClick={() => setTab(1)}>
                                        {financeText("View Customer Invoices")}
                                    </MDButton>
                                </MDBox>

                                {supplierInvoices.length ? (
                                    <MDBox sx={tableWrapperSx}>
                                        <DataTable
                                            table={supplierInvoiceTable}
                                            canSearch
                                            entriesPerPage
                                            showTotalEntries
                                            isSorted
                                            noEndBorder
                                        />
                                    </MDBox>
                                ) : (
                                    <EmptyState
                                        title={financeText("No supplier invoice found.")}
                                        icon={<UploadFile />}
                                    />
                                )}
                            </>
                        )}

                        {tab === 4 && (
                            <>
                                <MDBox
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    flexDirection={{ xs: "column", md: "row" }}
                                    gap={1.5}
                                    mb={2.5}
                                >
                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {financeText("Documents")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Finance and shipment documents associated with this job.")}
                                        </MDTypography>
                                    </MDBox>

                                    <MDButton
                                        variant="contained"
                                        sx={{
                                            borderRadius: "10px",
                                            background: `${COLORS.navy} !important`,
                                            color: "#FFFFFF !important",
                                        }}
                                    >
                                        <Description sx={{ mr: 0.5 }} />
                                        {financeText("Upload Document")}
                                    </MDButton>
                                </MDBox>

                                {documents.length ? (
                                    <MDBox sx={tableWrapperSx}>
                                        <DataTable
                                            table={documentTable}
                                            canSearch
                                            entriesPerPage
                                            showTotalEntries
                                            isSorted
                                            noEndBorder
                                        />
                                    </MDBox>
                                ) : (
                                    <EmptyState
                                        title={financeText("No document found.")}
                                        icon={<Description />}
                                    />
                                )}
                            </>
                        )}

                        {tab === 5 && (
                            <Grid container spacing={2.5}>
                                {[
                                    {
                                        title: "Operational Cost",
                                        value: operationalCost,
                                        tone: "red",
                                        icon: <ArrowDownward />,
                                    },
                                    {
                                        title: "Buying Rate",
                                        value: buyingRate,
                                        tone: "amber",
                                        icon: <AccountBalanceWallet />,
                                    },
                                    {
                                        title: "Revenue Variance",
                                        value: revenue - expectedRevenue,
                                        tone: revenue - expectedRevenue < 0 ? "red" : "green",
                                        icon: <ArrowUpward />,
                                    },
                                    {
                                        title: "Cost Variance",
                                        value: cost - expectedCost,
                                        tone: cost - expectedCost > 0 ? "red" : "green",
                                        icon: <ArrowDownward />,
                                    },
                                    {
                                        title: "Profit Variance",
                                        value: profit - expectedProfit,
                                        tone: profit - expectedProfit < 0 ? "red" : "green",
                                        icon: <Paid />,
                                    },
                                ].map((item) => (
                                    <Grid item xs={12} md={4} key={item.title}>
                                        <MetricCard
                                            title={item.title}
                                            value={formatDisplayCompact(item.value, currency)}
                                            subtitle={financeText("Actual minus expected")}
                                            icon={item.icon}
                                            tone={item.tone}
                                            delta={0}
                                        />
                                    </Grid>
                                ))}

                                <Grid item xs={12}>
                                    <Card sx={{ ...cardSx, boxShadow: "none" }}>
                                        <MDBox p={3}>
                                            <MDBox
                                                display="flex"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                mb={1.2}
                                            >
                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {financeText("Margin Performance")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    sx={{
                                                        color:
                                                            margin >= 20
                                                                ? COLORS.green
                                                                : margin >= 10
                                                                    ? COLORS.amber
                                                                    : COLORS.red,
                                                    }}
                                                >
                                                    {formatPercent(margin)}
                                                </MDTypography>
                                            </MDBox>

                                            <LinearProgress
                                                variant="determinate"
                                                value={Math.min(Math.max(margin, 0), 100)}
                                                sx={{
                                                    height: 10,
                                                    borderRadius: "999px",
                                                    backgroundColor: "#EEF2F6",
                                                    "& .MuiLinearProgress-bar": {
                                                        borderRadius: "999px",
                                                        background:
                                                            margin >= 20
                                                                ? COLORS.green
                                                                : margin >= 10
                                                                    ? COLORS.amber
                                                                    : COLORS.red,
                                                    },
                                                }}
                                            />
                                        </MDBox>
                                    </Card>
                                </Grid>
                            </Grid>
                        )}

                        {tab === 6 && (
                            <MDBox>
                                <MDBox display="flex" alignItems="center" gap={1} mb={2.5}>
                                    <MDBox
                                        width={38}
                                        height={38}
                                        borderRadius="11px"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        sx={{
                                            color: COLORS.purple,
                                            background: COLORS.purpleLight,
                                            border: "1px solid #D9D6FE",
                                        }}
                                    >
                                        <Timeline sx={{ fontSize: 19 }} />
                                    </MDBox>

                                    <MDBox>
                                        <MDTypography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: COLORS.text }}
                                        >
                                            {financeText("Activity Log")}
                                        </MDTypography>

                                        <MDTypography
                                            variant="caption"
                                            sx={{ color: COLORS.subtext }}
                                        >
                                            {financeText("Financial actions and workflow history.")}
                                        </MDTypography>
                                    </MDBox>
                                </MDBox>

                                {activities.length ? (
                                    <MDBox
                                        sx={{
                                            position: "relative",
                                            pl: 4,
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                left: 13,
                                                top: 8,
                                                bottom: 8,
                                                width: 2,
                                                background: "#E4E7EC",
                                            },
                                        }}
                                    >
                                        {activities.map((item, index) => (
                                            <MDBox
                                                key={`${item?.title || item?.action || "activity"}-${index}`}
                                                position="relative"
                                                py={1.5}
                                                borderBottom={
                                                    index === activities.length - 1
                                                        ? "none"
                                                        : `1px solid ${COLORS.border}`
                                                }
                                            >
                                                <MDBox
                                                    position="absolute"
                                                    left={-31}
                                                    top={24}
                                                    width={12}
                                                    height={12}
                                                    borderRadius="50%"
                                                    sx={{
                                                        background: COLORS.blue,
                                                        border: "3px solid #EFF8FF",
                                                    }}
                                                />

                                                <MDTypography
                                                    variant="button"
                                                    fontWeight="bold"
                                                    sx={{ color: COLORS.text }}
                                                >
                                                    {item?.title || item?.action || financeText("Activity")}
                                                </MDTypography>

                                                <MDTypography
                                                    variant="caption"
                                                    display="block"
                                                    mt={0.35}
                                                    sx={{ color: COLORS.subtext }}
                                                >
                                                    {item?.createdAt
                                                        ? new Date(item.createdAt).toLocaleString("tr-TR")
                                                        : ""}
                                                </MDTypography>
                                            </MDBox>
                                        ))}
                                    </MDBox>
                                ) : (
                                    <EmptyState title={financeText("No activity found.")} icon={<Timeline />} />
                                )}
                            </MDBox>
                        )}
                    </MDBox>
                </Card>
                <Card
                    sx={{
                        ...cardSx,
                        mt: 2.5,
                        background:
                            "#0B1F3A",
                        color: "#FFFFFF",
                        position: "relative",
                        overflow: "hidden",
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            width: 260,
                            height: 260,
                            borderRadius: "50%",
                            right: -90,
                            top: -120,
                            background: "rgba(255,255,255,.06)",
                        },
                    }}
                >
                    <MDBox p={{ xs: 2.5, md: 3 }} position="relative" zIndex={1}>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} lg={8}>
                                <MDBox display="flex" alignItems="center" gap={1} mb={1.2}>
                                    <WorkspacePremium sx={{ color: "#B2DDFF" }} />
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{
                                            color: "rgba(255,255,255,.72)",
                                            letterSpacing: ".1em",
                                        }}
                                    >
                                        {financeText("EXECUTIVE FINANCE SUMMARY")}
                                    </MDTypography>
                                </MDBox>

                                <MDTypography
                                    variant="h5"
                                    fontWeight="bold"
                                    sx={{ color: "#FFFFFF" }}
                                >
                                    {financeHealth >= 75
                                        ? financeText("This finance job is in a strong position.")
                                        : financeHealth >= 50
                                            ? financeText("This finance job requires controlled follow-up.")
                                            : financeText("This finance job requires immediate financial attention.")}
                                </MDTypography>

                                <MDTypography
                                    variant="body2"
                                    mt={1}
                                    sx={{
                                        color: "rgba(255,255,255,.72)",
                                        maxWidth: 820,
                                    }}
                                >
                                    {financeText("Revenue is")}{" "}
                                    {financeText(revenueDelta >= 0 ? "above" : "below")}{" "}
                                    {financeText("expected by")}{" "}
                                    {Math.abs(revenueDelta).toFixed(1)}%.{" "}
                                    {financeText("Collection performance is")}{" "}
                                    {collectionRate.toFixed(1)}%,{" "}
                                    {financeText("while open receivables stand at")}{" "}
                                    {formatDisplayCompact(receivable, currency)}{" "}
                                    {financeText("and supplier exposure at")}{" "}
                                    {formatDisplayCompact(openPayable, currency)}.{" "}
                                    {financeText("Total cost is")}{" "}
                                    {formatDisplayCompact(cost, currency)},{" "}
                                    {financeText("including")}{" "}
                                    {formatDisplayCompact(buyingRate, currency)}{" "}
                                    {financeText("buying rate")}.
                                </MDTypography>
                            </Grid>

                            <Grid item xs={12} lg={4}>
                                <MDBox
                                    p={2}
                                    borderRadius="10px"
                                    textAlign="center"
                                    sx={{
                                        background: "rgba(255,255,255,.09)",
                                        border: "1px solid rgba(255,255,255,.13)",
                                    }}
                                >
                                    <MDTypography
                                        variant="caption"
                                        fontWeight="bold"
                                        sx={{ color: "rgba(255,255,255,.68)" }}
                                    >
                                        {financeText("FINANCE HEALTH SCORE")}
                                    </MDTypography>

                                    <MDTypography
                                        variant="h2"
                                        fontWeight="bold"
                                        mt={0.7}
                                        sx={{ color: "#FFFFFF" }}
                                    >
                                        {financeHealth}
                                    </MDTypography>

                                    <LinearProgress
                                        variant="determinate"
                                        value={financeHealth}
                                        sx={{
                                            mt: 1.4,
                                            height: 8,
                                            borderRadius: "999px",
                                            backgroundColor: "rgba(255,255,255,.14)",
                                            "& .MuiLinearProgress-bar": {
                                                borderRadius: "999px",
                                                background:
                                                    financeHealth >= 75
                                                        ? "#75E0A7"
                                                        : financeHealth >= 50
                                                            ? "#FEDF89"
                                                            : "#FECDCA",
                                            },
                                        }}
                                    />
                                </MDBox>
                            </Grid>
                        </Grid>
                    </MDBox>
                </Card>
            </MDBox>

            <Dialog
                open={incomingLinkOpen}
                onClose={closeIncomingLinkDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: "10px" } }}
            >
                <DialogTitle sx={{ fontWeight: "bold", color: COLORS.text }}>
                    {financeText("Link Incoming Invoice")}
                </DialogTitle>

                <DialogContent dividers>
                    <MDTypography variant="caption" display="block" mb={2} sx={{ color: COLORS.subtext }}>
                        {financeText("Link an EDM incoming invoice to this job.")}
                    </MDTypography>

                    {linkIncomingError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {linkIncomingError}
                        </Alert>
                    )}

                    <TextField
                        fullWidth
                        size="small"
                        placeholder={financeText("Search by supplier or invoice no...")}
                        value={incomingSearchQuery}
                        onChange={(event) => setIncomingSearchQuery(event.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" sx={{ color: COLORS.subtext }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 2 }}
                    />

                    {loadingIncoming ? (
                        <MDBox display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={26} />
                        </MDBox>
                    ) : incomingError ? (
                        <Alert severity="error">{incomingError}</Alert>
                    ) : filteredIncomingInvoices.length === 0 ? (
                        <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
                            <CloudDownload sx={{ fontSize: 32, color: COLORS.subtext }} />
                            <MDTypography variant="button" sx={{ color: COLORS.subtext }}>
                                {financeText("No matching incoming invoices found.")}
                            </MDTypography>
                        </MDBox>
                    ) : (
                        <MDBox
                            display="flex"
                            flexDirection="column"
                            gap={1}
                            sx={{ maxHeight: 320, overflowY: "auto" }}
                        >
                            {filteredIncomingInvoices.map((item) => {
                                const isSelected = selectedIncoming?.uuid === item.uuid;

                                return (
                                    <MDBox
                                        key={item.uuid}
                                        onClick={() => setSelectedIncoming(item)}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: "10px",
                                            cursor: "pointer",
                                            border: `1px solid ${isSelected ? COLORS.blue : COLORS.border}`,
                                            background: isSelected ? COLORS.blueLight : COLORS.white,
                                            transition: "all .15s ease",
                                        }}
                                    >
                                        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                                            <MDBox minWidth={0}>
                                                <MDTypography variant="button" fontWeight="bold" display="block" sx={{ color: COLORS.text }}>
                                                    {item.supplierName || financeText("Unknown Job")}
                                                </MDTypography>
                                                <MDTypography variant="caption" display="block" sx={{ color: COLORS.subtext }}>
                                                    {item.id} · {formatDate(item.issueDate)}
                                                </MDTypography>
                                            </MDBox>
                                            <MDTypography variant="button" fontWeight="bold" sx={{ color: COLORS.text, flexShrink: 0 }}>
                                                {formatCurrency(item.amount, item.currency)}
                                            </MDTypography>
                                        </MDBox>
                                    </MDBox>
                                );
                            })}
                        </MDBox>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <MDButton variant="text" color="dark" onClick={closeIncomingLinkDialog} disabled={linkingIncoming}>
                        {financeText("Cancel")}
                    </MDButton>
                    <MDButton
                        variant="contained"
                        onClick={submitIncomingLink}
                        disabled={linkingIncoming || !selectedIncoming}
                        sx={{
                            background: `${COLORS.navy} !important`,
                            color: "#FFFFFF !important",
                        }}
                    >
                        {linkingIncoming ? <CircularProgress size={18} sx={{ color: "#FFFFFF" }} /> : financeText("Link")}
                    </MDButton>
                </DialogActions>
            </Dialog>

            <Footer />
        </DashboardLayout>
    );
};

export default FinanceJobDetail;